import { Component, DestroyRef, OnInit, OnDestroy, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { InputNumber } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Subject, filter, takeUntil, map, distinctUntilChanged, skip } from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { MappingStatusService } from '../../../services/mbrs/mapping-status.service';
import { CrossReferenceService } from '../../../services/mbrs/cross-reference.service';
import { CalculationService } from '../../../services/mbrs/calculation.service';
import { CrossTabLayoutChangeNotifierService } from '../../../services/mbrs/cross-tab-layout-change-notifier.service';
import { EqualityValidationService, EqualityValidationError } from '../../../services/mbrs/equality-validation.service';
import { differsAtCentPrecision } from '../../../services/mbrs/validation-field-key.util';
import { ColumnSumValidationService, ColumnSumValidationConfig } from '../../../services/mbrs/column-sum-validation.service';
import { EquityReconciliationService } from '../../../services/mbrs/equity-reconciliation.service';
import { ValidationContextService } from '../../../services/mbrs/validation-context.service';
import { FormSchemaService } from '../../../services/mbrs/form-schema.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { adoptIntoInlineEditSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  FORM_CALCULATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import { buildEditModeLivePipeline } from '../../../utils/form-edit-pipeline.util';
import { pickSubset } from '../../../utils/pick-subset.util';
import { isRowVisible, isCollapsibleHeader as isCollapsibleHeaderRule, headerLevelClass } from '../../../utils/section-visibility.util';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { NegativeHighlightDirective } from '../../../directives/negative-highlight.directive';
import { SchemaNumericDirective } from '../../../directives/schema-numeric.directive';
import { NumericFieldPolicy, resolveNumericPolicy } from '../../../utils/numeric-policy.util';
import { SkeletonModule } from 'primeng/skeleton';
import { ReloadPromptComponent } from '../../common/reload-prompt/reload-prompt.component';
import { ReloadBannerComponent } from '../../common/reload-banner/reload-banner.component';
import { stripAllValidatorsRecursively } from '../../../utils/form-validation.util';
import { DebugLayoutToggleService } from '../../../services/common/debug-layout-toggle.service';
import { DebugErrorCompressionToggleService } from '../../../services/common/debug-error-compression-toggle.service';
import { DebugColumnPagingToggleService } from '../../../services/common/debug-column-paging-toggle.service';
import { Role19DebugLayoutComponent, Role19DebugHost } from './role19-debug-layout/role19-debug-layout.component';
import {
  pickRole19SchemaFile,
  isRole19StaticallyRequired,
  role19ReferencedSheetKeys,
  role19SchemaForReferencedSheet,
  Role19SchemaControl,
} from '../../../services/mbrs/validators/role19-statement-of-changes-in-equity.rules';
import {
  showConsolidated as showConsolidatedCells,
  hasPreviousYear as hasPreviousYearCells,
} from '../../../services/mbrs/validators/dimension-visibility.util';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { recomputeReferencedSheets } from '../../../services/mbrs/cross-sheet-recompute.util';
import {
  } from '../../../utils/toast-durations.util';
import { EXTRACTED_SUM_MARKER } from '../../../services/mbrs/extracted-sum-warning.service';
import { isControlMandatory } from '../../../utils/required-field.util';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { FieldLinkLabelComponent } from '../../common/field-link-label/field-link-label.component';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { computedControlPredicate, revealValidationErrors, enableAndRevealErrors } from '../../../utils/validation-reveal.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';

/**
 * The mandatory-message body written by `validateRequiredFields()`, and the
 * discriminator every reader matches on. Producer and consumers share ONE
 * constant so they cannot drift — the same posture role 15 takes with
 * `MISMATCH_CORE` / `SOCI_SHEET_LABEL`.
 */
const MANDATORY_MESSAGE = 'This field is mandatory.';

interface EquityColumn {
  qname: string;
  label: string;
  order: number;
  calculated?: boolean;
}

interface FieldControl {
  qname: string;
  label: string;
  type: string;
  ui: {
    controlType: string;
    currency?: string;
    placeholder?: string;
    dualColumn?: boolean;
    equityColumns?: boolean;
    calculated?: boolean;
    readonly?: boolean;
    calculation?: string;
    level?: number;
    note?: string;
    decimalPlaces?: number;
    required?: boolean;
    linkTo?: {
      role: string;
      qname: string;
    };
  };
}

interface FormSchema {
  roleId: string;
  roleUri: string;
  label: string;
  taxonomy: string;
  sheetId: string;
  description: string;
  columnSumValidations?: ColumnSumValidationConfig[];
  equityColumns?: EquityColumn[];
  controls: FieldControl[];
}

@Component({
  selector: 'app-role19-610000-statement-of-changes-in-equity',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    FieldLinkLabelComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputNumber,
    ButtonModule,
    Toast,
    SubmitForValidationComponent,
    NegativeHighlightDirective,

    SchemaNumericDirective,
    SkeletonModule,
    ReloadPromptComponent,
    ReloadBannerComponent,
    Role19DebugLayoutComponent,
    FieldLinkTargetDirective
  ],
  providers: [MessageService],
  templateUrl: './role19-610000-statement-of-changes-in-equity.component.html',
  styleUrls: ['./role19-610000-statement-of-changes-in-equity.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Role19610000StatementOfChangesInEquityComponent implements OnInit, OnDestroy, Role19DebugHost {
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private stateService = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);
  private calculationService = inject(CalculationService);
  private equalityValidation = inject(EqualityValidationService);
  private columnSumValidation = inject(ColumnSumValidationService);
  private equityReconciliation = inject(EquityReconciliationService);
  private validationContext = inject(ValidationContextService);
  private formSchemaService = inject(FormSchemaService);
  private router = inject(Router);
  protected envService = inject(EnvironmentService);
  protected debugToggle = inject(DebugLayoutToggleService);
  protected debugErrorCompression = inject(DebugErrorCompressionToggleService);
  protected debugColumnPaging = inject(DebugColumnPagingToggleService);
  private layoutChangeNotifier = inject(CrossTabLayoutChangeNotifierService);
  private mappingStatus = inject(MappingStatusService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  private readonly sheetKey = SHEET_KEYS.statementOfChangesInEquity;

  showReloadPrompt = signal<boolean>(false);
  reloadPromptDismissed = signal<boolean>(false);

  statementOfChangesInEquityForm!: FormGroup;
  schema: FormSchema | null = null;
  isLoading = signal(true);
  isSyncValidationComplete = signal(false);
  isAsyncValidationComplete = false;
  showConsolidatedColumns = true; // Show Consolidated columns (determined by Role-0 Nature of financial statements)
  hasPreviousYear = false;
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Statement of Changes in Equity (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.statementOfChangesInEquity
    );
  }

  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  expandedSections = new Map<string, Set<string>>(); // Track which sections are expanded per table (tableKey -> Set<qname>)

  /** Bound as a property so the directive gets a stable, `this`-correct function. */
  protected readonly isSectionHeaderFn = (control: any): boolean =>
    this.isSectionHeader(control);
  private editingRows = new Map<string, { [key: string]: number | null }>(); // Track rows in edit mode with original values
  private calculatedTotalKeys: string[] = [];

  /** Row Mapping-Status frozen at edit-enter; while a row is mid-edit its badge
   *  shows this committed value (not the live in-edit recompute) so the pencil
   *  alone never flips it to 'Edited'. Cleared on Tick/Cancel → badge recomputes.
   *  Key = rowValidationStatusCache key: `${qname}::${consolidationType}::${periodType}` */
  private frozenRowStatus = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();

  private validationErrors = new Map<string, string[]>();
  private equalityValidationErrors = new Map<string, EqualityValidationError[]>(); // Track equality validation errors

  // Cell-level validation errors for Non-distributable reserves sum check (MPERS only)
  private columnSumErrors = new Map<string, string>();

  // Cell-level validation errors for equity total horizontal check (MFRS Consolidated only)
  private equityTotalErrors = new Map<string, string>();
  
  // ─── Validation Caches (rebuilt after each validation pass) ──────────
  // These caches store pre-computed results so template bindings do Map.get() instead of recomputing
  private rowValidationStatusCache = new Map<string, 'Failed' | 'Edited' | 'Validated*' | 'Validated'>();
  private rowErrorDescriptionCache = new Map<string, string>();
  // Parallel to rowErrorDescriptionCache: holds the {errors, warnings} counts
  // for the SAME row+table key, populated by _computeRowErrorDescription at the
  // point its errors[]/suggestedChanges[] arrays are finalized so the counts
  // can never drift from the rendered list. Consumed by getRowErrorCounts()
  // for the Ctrl+Alt+E error-compression toggle (non-production only).
  private rowErrorCountCache = new Map<string, { errors: number; warnings: number }>();
  private cellValidationCache = new Map<string, { crossSheet: boolean, required: boolean, columnSum: boolean, equityTotal: boolean }>();
  private rowHasValidationErrorCache = new Map<string, boolean>();
  // Row background colour intent ('error' → red, 'warning' → yellow, 'none').
  // Derived from the SAME hasError / hasSuggestedChangeWarning flags as the
  // Mapping Status, but WITHOUT the edit-mode (forceEdited → 'Edited') override,
  // so entering edit mode never repaints a row. Red outranks yellow.
  private rowColorIntentCache = new Map<string, 'error' | 'warning' | 'none'>();

  // Cell-level validation errors for Equity Beginning/End period mismatch
  private equityBeginningEndErrors = new Map<string, string>();

  // Tracks qnames added to validationErrors by cross-sheet sub-checks so that
  // validateSameSheet() can clear only the same-sheet partition without wiping
  // frozen cross-sheet errors from the last Tick/cross-sheet listener run.
  private crossSheetErrorQnames = new Set<string>();

  // Same-sheet equity roll-forward errors (consecutiveYearEquity +
  // consecutiveYearEquityTotal), kept in their OWN map — parallel to the
  // cross-sheet `equalityValidationErrors` — so the two can COEXIST on a shared
  // qname (Equity:End). The Total/end-of-period/previous-year cell legitimately
  // fails two distinct checks at once: ≠ SOFP Total Equity (Previous Year)
  // [cross-sheet, validateCrossSheetEquityByYear] AND ≠ Equity at beginning of
  // period (Current Year) [same-sheet roll-forward]. Before this map, each pass's
  // preserve-filter whitelisted only extracted-sum/sum/mandatory, so whichever
  // equality check ran last stomped the other's message. rebuildEqualityMessages()
  // re-sources both from their authoritative maps so neither clobbers the other.
  private sameSheetEqualityErrors = new Map<string, EqualityValidationError[]>();

  // Unconditional [#f3]-vs-[#f22] begin/end cell-check messages (the spec's CANONICAL
  // same-sheet roll-forward source — see `updateSameSheetBeginningEndErrors`). Kept in
  // its OWN authoritative map — parallel to `sameSheetEqualityErrors` (the gated subset)
  // and `equalityValidationErrors` (cross-sheet) — so `rebuildEqualityMessages` can
  // re-source it and it survives the cross-sheet preserve-filter on a shared key
  // (`Equity:Beginning` / `Equity:End`). Before this map it was a direct write into
  // `validationErrors` that the preserve-filter permanently dropped whenever the same key
  // also carried a cross-sheet error → the cell showed only a red border, no message.
  private sameSheetBeginningEndErrors = new Map<string, EqualityValidationError[]>();

  // Generation counter to prevent stale async callbacks from overwriting newer equality validation results
  private equalityValidationGeneration = 0;

  // Track setTimeout handles so they can be cancelled on destroy
  // Prevents late state-service writes when the component is destroyed during background validation
  private initTimeoutIds: ReturnType<typeof setTimeout>[] = [];

  /**
   * Internal version signal used under OnPush to notify the template that
   * non-signal mutable state (validationErrors, equalityValidationErrors,
   * columnSumErrors, equityTotalErrors, equityBeginningEndErrors,
   * expandedSections, rowValidationStatusCache, rowErrorDescriptionCache,
   * rowErrorCountCache, rowHasValidationErrorCache, cellValidationCache,
   * editingRows) has
   * changed. Template-called helper methods read this signal at the top
   * so that bumping it re-evaluates all bindings that depend on those
   * Maps/Sets.
   */
  private viewVersion = signal(0);

  private bumpView(): void {
    this.viewVersion.update((version) => version + 1);
  }

  /**
   * Get currency symbol with unit from Role-1
   * e.g., "RM'000", "$'000", "€'000"
   */
  get currencyUnit(): string {
    const symbol = this.crossRefService.getCurrencySymbol();
    return `${symbol}'000`;
  }

  /**
   * Get formatted date range for current year
   * e.g., "01/01/2024 - 31/12/2024"
   */
  get periodHeaderCurrent(): string {
    return this.crossRefService.getFormattedFinancialYear('current');
  }

  /**
   * Get formatted date range for previous year
   * e.g., "01/01/2023 - 31/12/2023"
   */
  get periodHeaderPrevious(): string {
    return this.crossRefService.getFormattedFinancialYear('previous');
  }

  ngOnInit(): void {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '19',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    this.checkConsolidatedStatus();
    this.loadSchema();
    this.debugToggle.bindGlobalShortcut(this.destroyRef);
    this.debugErrorCompression.bindGlobalShortcut(this.destroyRef);
    this.debugColumnPaging.bindGlobalShortcut(this.destroyRef);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Cancel pending init timeouts to prevent late state-service writes
    // after the component is destroyed during background validation cycle
    for (const id of this.initTimeoutIds) {
      clearTimeout(id);
    }
    this.initTimeoutIds = [];
  }

  /**
   * Initialize all sections as expanded by default for all tables
   */
  private initializeExpandedSections(): void {
    // Start with all level 2, 3, 4, and 5 sections expanded for each table
    if (this.schema) {
      const consolidationTypes = this.showConsolidatedColumns ? ['Consolidated', 'Separate'] : ['Separate'];
      const periodTypes = this.hasPreviousYear ? ['Current', 'Previous'] : ['Current'];

      consolidationTypes.forEach(consolidationType => {
        periodTypes.forEach(periodType => {
          const tableKey = `${consolidationType}_${periodType}`;
          const expandedSet = new Set<string>();

          // Seed every section header (all levels, incl. level 1) as expanded.
          // Level 1 is seeded because the shared `isRowVisible` helper consults
          // level-1 ancestors; without it, level-1 would read as collapsed and
          // hide everything on load. Seeding by `isSectionHeader` (not a level
          // allow-list) excludes any DATA fields that carry a `ui.level`.
          this.schema!.controls.forEach(control => {
            if (this.isSectionHeader(control)) {
              expandedSet.add(control.qname);
            }
          });

          this.expandedSections.set(tableKey, expandedSet);
        });
      });
    }
  }

  /**
   * Toggle section expansion for a specific table
   */
  toggleSection(qname: string, consolidationType: string, periodType: string): void {
    const tableKey = `${consolidationType}_${periodType}`;
    let expandedSet = this.expandedSections.get(tableKey);

    if (!expandedSet) {
      expandedSet = new Set<string>();
      this.expandedSections.set(tableKey, expandedSet);
    }

    if (expandedSet.has(qname)) {
      expandedSet.delete(qname);
    } else {
      expandedSet.add(qname);
    }
  }

  /**
   * Check if section is expanded for a specific table
   */
  isSectionExpanded(qname: string, consolidationType: string, periodType: string): boolean {
    this.viewVersion();
    const tableKey = `${consolidationType}_${periodType}`;
    const expandedSet = this.expandedSections.get(tableKey);
    return expandedSet ? expandedSet.has(qname) : false;
  }

  /**
   * Check if a row should be shown based on parent section expansion for a
   * specific (consolidation × period) table.
   *
   * Delegates to the shared, pure `isRowVisible` helper (monotonic
   * strictly-decreasing ancestor walk). The per-table expansion state is bridged
   * via the `isSectionExpanded(qname, consolidationType, periodType)` closure,
   * and `isSectionHeader` excludes DATA fields that carry a `ui.level`. This
   * replaces the prior per-level walk whose header branches only checked the
   * IMMEDIATE parent (e.g. a level-4 header ignored a collapsed level-2
   * grandparent) — the cause of "collapsing a parent hides one subsection's
   * rows but not its siblings'". See `section-visibility.util.ts`.
   *
   * `this.viewVersion()` is read up-front to keep this row's render bound to the
   * manual change-detection version signal even for rows with no ancestors
   * (where the `isSectionExpanded` closure — which also reads it — is not hit).
   */
  shouldShowRow(control: FieldControl, index: number, consolidationType: string, periodType: string): boolean {
    this.viewVersion();
    if (!this.schema) return true;
    return isRowVisible(
      this.schema.controls,
      index,
      (qname) => this.isSectionExpanded(qname, consolidationType, periodType),
      (control) => this.isSectionHeader(control)
    );
  }

  /**
   * The four (dimension × period) visibility cells for this sheet, derived by
   * the shared, config-driven resolver via CrossReferenceService.getVisibility().
   * The SAME resolver + central registry feeds the pure
   * role19StatementOfChangesInEquityValidator (through its
   * `resolveRole19ColumnVisibility` adapter), so the mounted editor and the
   * headless listing cannot drift on which columns are shown/validated.
   *
   * NOTE: the build-time layout (`showConsolidatedColumns` / `hasPreviousYear`)
   * remains a FROZEN snapshot taken in checkConsolidatedStatus() at ngOnInit —
   * the cached FormGroup is built around those frozen values and
   * detectLayoutFlagsChanged() compares fresh-vs-frozen to surface the reload
   * prompt. Converting them to live getters would break that one-way-door
   * invariant (fresh would always equal the getter, so the prompt could never
   * fire), so they stay fields. This getter exists for parity with the other
   * dimension-gated sheets and as the single read point should a future change
   * drop the reload-prompt model.
   */
  get dimensionVisibility() {
    return this.crossRefService.getVisibility(SHEET_KEYS.statementOfChangesInEquity);
  }

  /**
   * Check consolidation status and comparative information from Role-0
   * CRITICAL: This method freezes the build-time column-visibility snapshot.
   *
   * Visibility is now derived by the SHARED, config-driven resolver
   * (`CrossReferenceService.getVisibility` → `dimension-visibility.util` +
   * registry), the same one the pure validator's adapter calls — so the editor
   * and the headless listing agree on which columns exist. The result is still
   * captured into the frozen `showConsolidatedColumns` / `hasPreviousYear`
   * fields (NOT live getters) because the cached FormGroup is built around them
   * and detectLayoutFlagsChanged() needs a build-time baseline to compare
   * against for the reload prompt.
   *
   * The resolver reads Nature / preparation-type prereq-first then filingInfo,
   * and defaults an unknown/unset Nature to "Consolidated shown" (the approved
   * pre-subscription posture, matching the validator).
   */
  private checkConsolidatedStatus(): void {
    const cells = this.dimensionVisibility;
    this.showConsolidatedColumns = showConsolidatedCells(cells);
    this.hasPreviousYear = hasPreviousYearCells(cells);
  }

  // Why: pure predicate, no mutation of showConsolidatedColumns / hasPreviousYear.
  // Under the reload-prompt model the component's layout MUST stay frozen
  // until the user reloads — mutating the flags here would break the
  // schema/data invariant the cached FormGroup relies on. Compare-only,
  // return boolean, let the notifier surface the prompt.
  //
  // The FRESH values are derived through the SAME shared resolver that
  // checkConsolidatedStatus() froze into the fields, so the comparison is
  // apples-to-apples (a unknown-nature default of "Consolidated shown" cannot
  // make the prompt fire spuriously). getVisibility() reads live filing slices,
  // so this still detects a genuine upstream layout change after build.
  private detectLayoutFlagsChanged(): boolean {
    const cells = this.dimensionVisibility;
    const freshShowConsolidated = showConsolidatedCells(cells);
    const freshPreviousYear = hasPreviousYearCells(cells);

    return (
      freshShowConsolidated !== this.showConsolidatedColumns ||
      freshPreviousYear !== this.hasPreviousYear
    );
  }

  private setupLayoutReactivity(): void {
    this.layoutChangeNotifier
      .watchSubmissionCommitted({
        sheetKey: this.sheetKey,
        slicesOfInterest: ['filingPrerequisites', 'filingInfo', 'scopeOfFiling'],
        detectChanged: () => this.detectLayoutFlagsChanged(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.reloadPromptDismissed()) {
          this.showReloadPrompt.set(true);
        }
      });
  }

  onReload(): void {
    window.location.reload();
  }

  onDismiss(): void {
    this.showReloadPrompt.set(false);
    this.reloadPromptDismissed.set(true);
    this.disableAllValidations();
  }

  onReturnToFiles(): void {
    this.showReloadPrompt.set(false);
    this.router.navigate(['/mbrs/xbrl-files']);
  }

  // Why: one-way door. Once the user dismisses the reload prompt the
  // in-memory form schema is known-stale vs. upstream scope; persisting
  // any further validation result would be misleading. We strip every
  // validator from every control (and the root FormGroup) so no spurious
  // errors accumulate before the user reloads. There is deliberately no
  // re-enable path — a fresh ngOnInit (via reload) is the only recovery.
  private disableAllValidations(): void {
    stripAllValidatorsRecursively(this.statementOfChangesInEquityForm);
  }

  /**
   * Load form schema from JSON
   * Dynamically loads MFRS or MPERS schema based on accounting standard from Role-0
   */
  private async loadSchema(): Promise<void> {
    this.isLoading.set(true);

    // Wait for retrieve-mbrs-data hydration so the accounting standard is
    // populated before schema selection — a mid-hydration refresh mount would
    // otherwise fall back to the MFRS default and MPERS values would never bind
    // (blank form).
    await this.stateService.waitForMbrsHydration('role19');
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.hydrationSettled);

    // Determine which schema to load based on accounting standard.
    // Delegate to the shared `pickRole19SchemaFile()` (which lives alongside
    // the pure validator) and pass the RAW accounting standard so the editor
    // and validator can never drift on the filename selection. The picker is
    // MFRS-default (MPERS only on the exact MPERS literal); collapsing to an
    // isMFRS boolean here would diverge from the validator for an
    // unknown/empty standard.
    const accountingStandard =
      this.crossRefService.getCrossReferenceData().accountingStandard;
    const schemaFile = pickRole19SchemaFile(accountingStandard);

    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    fetchSheetSchema<FormSchema>(
        this.loadTraceHandle,
        `assets/forms/${schemaFile}`,
      )
      .then(async (data: FormSchema) => {
        this.schema = data;
        this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log('Role-19 schema loaded:', this.schema);

        // Preload schemas for sheets referenced by equality validation rules
        // so that error messages show field labels instead of raw qnames
        // IMPORTANT: await to ensure schemas are cached before validation runs
        const currentTaxonomy: 'MFRS' | 'MPERS' = this.crossRefService.isMFRS() ? 'MFRS' : 'MPERS';
        return this.validationContext.preloadReferencedSchemas(this.schema, currentTaxonomy);
      })
      .then(() => {
        this.initializeExpandedSections(); // Initialize expand/collapse state after schema is loaded
        // Tell the Ctrl+Alt+T column-paging service how many equity columns
        // exist so it can clamp its page window. No-op effect in production
        // (the service never enables there). Safe for the dual-column schema
        // too (0 equity columns → service has nothing to page).
        this.debugColumnPaging.setColumnCount(this.schema?.equityColumns?.length ?? 0);
        this.buildForm();
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
        this.setupEditPipeline(); // Setup reactive calculations, auto-save, validation, and status

        // Reveal the sheet's already-computed errors now that the form exists
        // and has been populated. Deterministic replacement for the filing-form
        // container's setTimeout-driven markAllAsTouched, which silently no-ops
        // when a sheet's async init has not yet assigned its FormGroup — see
        // revealValidationErrors' docblock. Touch-only: cannot move the verdict.
        revealValidationErrors(this.statementOfChangesInEquityForm, {
          isComputed: computedControlPredicate(this.schema?.controls),
        });

        // Hydration window: the synchronous portion of loadSavedState performs
        // a direct updateSheetData() write that is NOT user-driven. Bracket so
        // notifyCrossTab is suppressed for the duration. The deferred
        // setTimeout(...) inside loadSavedState extends past this synchronous
        // window, so loadSavedState itself re-brackets the deferred write.
        this.stateService.beginHydration();
        try {
          this.loadSavedState();
          this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored); // markSheetInitializedWithStatus is called inside loadSavedState after validations
        } finally {
          this.stateService.endHydration();
        }
        this.setupStatePersistence();
        this.setupLayoutReactivity();
        this.isLoading.set(false);
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);

        // Snapshot baseline after all debounced valueChanges settle
        this.initTimeoutIds.push(setTimeout(() => {
          this.stateService.markSheetInitialized(SHEET_KEYS.statementOfChangesInEquity);
        }, 600));
      })
      .catch(error => {
        console.error('Error loading schema:', error);
        // Classified rather than reported as a bare schema failure: this one
        // `.catch` also covers the cross-sheet preload, buildForm, the edit
        // pipeline and loadSavedState, so "Failed to load form schema" named
        // the wrong step for most of what can land here.
        this.loadTraceHandle.fail(classifySheetInitError(error), error);
        this.isLoading.set(false);
        this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
      });
  }

  /**
   * Build reactive form from schema
   * CRITICAL: For equityColumns mode, creates FormControls per equity column
   *           For dualColumn mode, creates 4 FormControls per field (Consolidated Current/Previous + Separate Current/Previous)
   */
  private buildForm(): void {
    if (!this.schema) return;

    const formControls: any = {};

    this.schema.controls.forEach(control => {
      // Skip headers
      if (this.isSectionHeader(control)) return;

      // Determine required validators
      const validators: ValidatorFn[] = [];

      // Use null for non-calculated fields, 0 for calculated/total fields
      // This ensures unmapped API data fields remain as NULL (not converted to 0)
      const defaultValue = control.ui.calculated ? 0 : null;

      if (control.ui.equityColumns && this.schema?.equityColumns) {
        // Create FormControls for 4 table variations (Consolidated/Separate x Current/Previous)
        // Format: {rowQname}_{columnQname}_{consolidationType}_{periodType}
        const consolidationTypes = this.showConsolidatedColumns ? ['Consolidated', 'Separate'] : ['Separate'];
        const periodTypes = this.hasPreviousYear ? ['Current', 'Previous'] : ['Current'];

        consolidationTypes.forEach(consolidationType => {
          periodTypes.forEach(periodType => {
            this.schema!.equityColumns!.forEach(column => {
              const controlName = this.getEquityColumnControlName(control.qname, column.qname, consolidationType, periodType);
              formControls[controlName] = new FormControl({ value: defaultValue, disabled: true }, validators);
            });
          });
        });
      } else if (control.ui.dualColumn) {
        // Always create 4 columns: Consolidated (Current, Previous) and Separate (Current, Previous)
        // All fields start disabled (editable fields can be enabled via edit mode)
        // Phase 3: Standard bracket format
        formControls[control.qname + '[Consolidated][CurrentYear]'] = new FormControl({ value: defaultValue, disabled: true }, validators);
        formControls[control.qname + '[Consolidated][PreviousYear]'] = new FormControl({ value: defaultValue, disabled: true }, validators);
        formControls[control.qname + '[Separate][CurrentYear]'] = new FormControl({ value: defaultValue, disabled: true }, validators);
        formControls[control.qname + '[Separate][PreviousYear]'] = new FormControl({ value: defaultValue, disabled: true }, validators);
      }
    });

    this.statementOfChangesInEquityForm = this.fb.group(formControls);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Role-19 form built with controls:', Object.keys(formControls).length);
    // console.log('Sample control names:', Object.keys(formControls).slice(0, 10));
    // console.log('showConsolidatedColumns:', this.showConsolidatedColumns);
    // console.log('hasPreviousYear:', this.hasPreviousYear);
    // console.log('hasEquityColumns:', this.hasEquityColumns());

    // Debug: Log form control names for subtotal columns
    const subtotalColumns = ['NonDistributableReserves', 'OtherReserves', 'EquityAttributableToOwners'];
    const subtotalControlNames = Object.keys(formControls).filter(key =>
      subtotalColumns.some(col => key.includes(col))
    );
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍 [buildForm] Subtotal column form controls:', subtotalControlNames.slice(0, 20));
    // console.log('🔍 [buildForm] Total subtotal controls:', subtotalControlNames.length);

    // Debug: Check how many controls have equityColumns flag
    const equityColumnsCount = this.schema.controls.filter(control => control.ui.equityColumns).length;
    const numberControlsCount = this.schema.controls.filter(control => control.ui.controlType === 'number').length;
    
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('Controls with equityColumns flag:', equityColumnsCount);
    // console.log('Controls with controlType=number:', numberControlsCount);
    // console.log('Total controls in schema:', this.schema.controls.length);
  }

  /**
   * Check if control is a section header (including TextBlock)
   */
  isSectionHeader(control: FieldControl): boolean {
    return control.type === 'SectionHeader' || control.type === 'TextBlock' || control.ui.controlType === 'section-header';
  }

  /**
   * Whether the section header at `index` is collapsible — renders a chevron and
   * toggles on click. Delegates to the shared, structural `isCollapsibleHeader`
   * rule (any header deeper than the banner that encloses rows — no level
   * ceiling, so it can't drift as the schema gains deeper headers). It is
   * column-agnostic: whether a header CAN collapse depends only on schema
   * structure, while whether its rows are currently visible stays a per-column
   * concern in `shouldShowRow`. Changes in Equity opts the level-1 title banner
   * back in (`minCollapsibleLevel: 1`) to preserve its collapse-whole-sheet
   * behaviour — and to make both per-column render blocks consistent (the second
   * block previously omitted level 1).
   */
  isCollapsibleHeader(index: number): boolean {
    return !!this.schema && isCollapsibleHeaderRule(
      this.schema.controls,
      index,
      (control) => this.isSectionHeader(control),
      { minCollapsibleLevel: 1 },
    );
  }

  /**
   * Get header CSS class based on level
   */
  getHeaderClass(level?: number | string): string {
    return headerLevelClass(level);
  }

  /**
   * Check if field is calculated
   */
  /**
   * Does this row's label get a `*`?
   *
   * Delegates to the shared `isControlMandatory` so this sheet cannot drift from
   * the other 16 — the marker used to live only inside
   * `<label class="field-label">`, which this sheet does not render, so its
   * mandatory rows went unmarked from the initial scaffolding until 2026-08-11.
   * See `isControlMandatory` in `required-field.util.ts`.
   *
   * `crossSheetValue` is supplied because a cross-sheet `requiredIf` degrades to
   * "not mandatory" without it; every other declaration form this sheet uses is
   * unconditional and needs no context. Same-sheet `selfData` is deliberately
   * NOT passed: this sheet keys its values per cell
   * (`qname[Dimension][Period]`), not by bare qname, so a same-sheet lookup
   * would silently miss. Add it — with the right key shape — if a same-sheet
   * conditional rule is ever declared here.
   */
  isFieldRequired(control: any): boolean {
    return isControlMandatory(control, {
      crossSheetValue: (sheet, field) =>
        (this.stateService.getSheetData(sheet as any) as any)?.[field],
    });
  }

  isCalculatedField(control: FieldControl): boolean {
    return control.ui.calculated === true;
  }

  /**
   * Check if label contains '(-)' indicating a negative/deduction field
   * Used to display such labels in red color
   */
  isNegativeLabel(control: FieldControl): boolean {
    return control.label?.includes('(-)') ?? false;
  }

  /**
   * Check if field is required based on schema.
   *
   * LB-001 fix-guard: the schema may declare a field's required-ness via
   *   (a) the top-level `control.required: true` flag, OR
   *   (b) the UI-level `control.ui.required: true` flag.
   * The pre-existing implementation only honoured (b), so a schema edit
   * that sets only (a) would silently fail to enforce. The shared
   * `isRole19StaticallyRequired()` helper in
   * role19-statement-of-changes-in-equity.rules.ts expands the check to
   * honour both — imported here so the editor and pure validator derive
   * requiredness from exactly one place. Section headers, calculated /
   * readonly rows, and non-MonetaryItemType rows are skipped by the helper
   * (they never carry a required FormControl in buildForm()).
   *
   * Today both schemas happen to set both flags together on every required
   * field, so this refactor is a no-op for current conversions; the guard
   * exists for any future schema edit.
   */
  isRequiredField(control: FieldControl): boolean {
    return isRole19StaticallyRequired(control as unknown as Role19SchemaControl);
  }

  /**
   * Check if a required field is empty for a specific control name
   * Returns true if the field is required and empty
   * Note: 0 is considered a valid value, not empty
   */
  private isRequiredFieldEmpty(controlName: string): boolean {
    const formControl = this.statementOfChangesInEquityForm.get(controlName);
    if (!formControl) return false;

    const value = formControl.value;
    // Consider empty if null, undefined, or empty string (0 is a valid value)
    return value === null || value === undefined || value === '';
  }

  /**
   * Validate required fields and add errors to validationErrors map
   * Called during form validation
   */
  private validateRequiredFields(): void {
    if (!this.schema) return;

    // Get all required controls from schema
    const requiredControls = this.schema.controls.filter(
      control => this.isRequiredField(control) && !this.isSectionHeader(control)
    );

    requiredControls.forEach(control => {
      const requiredErrors: string[] = [];

      if (this.hasEquityColumns() && this.schema?.equityColumns) {
        // Equity columns mode: Check each equity column across all tables
        const consolidationTypes = this.showConsolidatedColumns ? ['Consolidated', 'Separate'] : ['Separate'];
        const periodTypes = this.hasPreviousYear ? ['Current', 'Previous'] : ['Current'];

        consolidationTypes.forEach(consolidationType => {
          periodTypes.forEach(periodType => {
            this.schema!.equityColumns!.forEach(column => {
              const controlName = this.getEquityColumnControlName(control.qname, column.qname, consolidationType, periodType);

              if (this.isRequiredFieldEmpty(controlName)) {
                const columnLabel = this.mandatoryColumnLabel(column.qname, consolidationType, periodType);
                requiredErrors.push(`${columnLabel}: ${MANDATORY_MESSAGE}`);
              }
            });
          });
        });
      } else {
        // Dual column mode: Check all 4 columns (Phase 3: bracket format)
        const columns = [
          { suffix: '[Consolidated][CurrentYear]', label: 'Consolidated Current' },
          { suffix: '[Consolidated][PreviousYear]', label: 'Consolidated Previous' },
          { suffix: '[Separate][CurrentYear]', label: 'Separate Current' },
          { suffix: '[Separate][PreviousYear]', label: 'Separate Previous' }
        ];

        columns.forEach(({ suffix, label }) => {
          const controlName = control.qname + suffix;

          if (this.isRequiredFieldEmpty(controlName)) {
            requiredErrors.push(`${label}: ${MANDATORY_MESSAGE}`);
          }
        });
      }

      // Add required errors to validation errors map
      if (requiredErrors.length > 0) {
        const existingErrors = this.validationErrors.get(control.qname) || [];

        // Filter out old required errors
        const nonRequiredErrors = existingErrors.filter(
          error => !error.includes(MANDATORY_MESSAGE)
        );

        // Combine with new required errors
        const combinedErrors = [...nonRequiredErrors, ...requiredErrors];
        this.validationErrors.set(control.qname, combinedErrors);
      } else {
        // Remove required errors if they exist, keep other errors
        const existingErrors = this.validationErrors.get(control.qname);
        if (existingErrors && existingErrors.length > 0) {
          const nonRequiredErrors = existingErrors.filter(
            error => !error.includes(MANDATORY_MESSAGE)
          );

          if (nonRequiredErrors.length > 0) {
            this.validationErrors.set(control.qname, nonRequiredErrors);
          } else {
            this.validationErrors.delete(control.qname);
          }
        }
      }
    });
  }

  /**
   * Check if a specific cell has a required field error (cache lookup)
   */
  hasRequiredError(control: FieldControl, columnQname: string, consolidationType: string, periodType: string): boolean {
    this.viewVersion();
    const key = `${control.qname}::${columnQname}::${consolidationType}::${periodType}`;
    return this.cellValidationCache.get(key)?.required ?? false;
  }

  /**
   * The per-cell prefix a mandatory message carries — the SINGLE definition,
   * written by {@link validateRequiredFields} and matched by
   * {@link _computeHasRequiredError}. Keeping one producer and one consumer on
   * one expression is what makes the border and the Error Description text
   * provably agree cell-for-cell.
   */
  private mandatoryColumnLabel(columnQname: string | undefined, consolidationType: string, periodType: string): string {
    if (this.hasEquityColumns() && this.schema?.equityColumns) {
      const column = this.schema.equityColumns.find(control => control.qname === columnQname);
      return `${column?.label ?? columnQname} [${consolidationType} ${periodType}]`;
    }
    // Dual-column (legacy) mode: "Consolidated Current", "Separate Previous", …
    return `${consolidationType} ${periodType}`;
  }

  /**
   * Compute if a specific cell has a required field error (full logic).
   * Used to add the red border to individual input fields.
   *
   * Answers from the SAME `validationErrors` mandatory messages the Error
   * Description column renders, matched on the shared
   * {@link mandatoryColumnLabel} prefix — P.O. ruling 2026-08-26: the border
   * appears whenever the field's error text appears, so the two must not be
   * derived from different sources.
   *
   * **Do not reinstate the `pristine && !touched` gate that stood here.** It
   * asked "has the preparer interacted with this cell?", which has no bearing on
   * whether a mandatory field is empty, and on THIS sheet it could essentially
   * never pass: every amount control is built `disabled: true` (the pencil
   * enables them), and `revealValidationErrors` — the mount-time pass that marks
   * a loaded sheet touched — deliberately skips disabled controls, making it a
   * no-op here. `enterEditMode()` enables without touching, too. So the border
   * could not paint on load, nor with the row open; it appeared only after
   * typing into that exact cell and vanished again on Tick
   * (`markAsPristine()` + `disable()`).
   *
   * The gate was added (2026-01-30) to stop borders lingering after
   * `Cancel → Confirm revert`. Reading `validationErrors` covers that case
   * exactly rather than by proxy: `onCancelConfirmed()` clears the map and
   * deliberately does NOT re-validate, so the text goes and the border goes with
   * it. Pinned by `role19-mandatory-cell-border.spec.ts`.
   */
  private _computeHasRequiredError(control: FieldControl, columnQname: string, consolidationType: string, periodType: string): boolean {
    if (!this.isRequiredField(control)) return false;

    const messages = this.validationErrors.get(control.qname);
    if (!messages || messages.length === 0) return false;

    const prefix = `${this.mandatoryColumnLabel(columnQname, consolidationType, periodType)}:`;
    return messages.some(msg => msg.startsWith(prefix) && msg.includes(MANDATORY_MESSAGE));
  }

  /**
   * Check if a specific cell has a non-distributable reserves sum validation error (cache lookup)
   */
  hasColumnSumError(control: FieldControl, columnQname: string, consolidationType: string, periodType: string): boolean {
    this.viewVersion();
    const key = `${control.qname}::${columnQname}::${consolidationType}::${periodType}`;
    return this.cellValidationCache.get(key)?.columnSum ?? false;
  }

  /**
   * Compute if a specific cell has a non-distributable reserves sum validation error (full logic)
   * Used to add red border to individual input fields for MPERS sub-total validation
   */
  private _computeHasColumnSumError(control: FieldControl, columnQname: string, consolidationType: string, periodType: string): boolean {
    // Check if this column is a sub-total column defined in schema's columnSumValidations
    if (!this.schema?.columnSumValidations || this.schema.columnSumValidations.length === 0) {
      return false;
    }

    // Check if the columnQname matches any subTotalColumnQname in the validation configs
    const isSubTotalColumn = this.schema.columnSumValidations.some(
      config => config.subTotalColumnQname === columnQname
    );
    if (!isSubTotalColumn) return false;

    const controlName = this.getEquityColumnControlName(control.qname, columnQname, consolidationType, periodType);
    return this.columnSumErrors.has(controlName);
  }

  /**
   * Live edit pipeline: valueChanges → debounce → calculate → validateSameSheet →
   * validateCrossSheet (display-only, no persist) → evaluateStatus.
   * No persist step — commits happen only on Tick (saveEditMode).
   */
  private setupEditPipeline(): void {
    buildEditModeLivePipeline({
      source$: this.statementOfChangesInEquityForm.valueChanges,
      debounceMs: FORM_CALCULATION_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      calculate: () => this.calculateTotals(),
      validateSameSheet: () => this.validateSameSheet(),
      validateCrossSheet: () => this.validateCrossSheet(),
      evaluateStatus: () => this.evaluateSheetStatus(),
    });
  }

  /**
   * Calculate totals for Statement of Changes in Equity
   * NOTE: Only vertical calculations (within columns) are performed
   *       Horizontal calculations (across columns) are DISABLED based on mtools behavior
   */
  private calculateTotals(): void {
    if (!this.schema) return;

    const formData = this.statementOfChangesInEquityForm.getRawValue();
    const xbrlPrefix: 'ifrs-full' | 'ifrs-smes' = this.crossRefService.isMFRS()
      ? 'ifrs-full'
      : 'ifrs-smes';

    // Build the visibility tuple matrix mirroring the previous in-component loops.
    // Dual-column mode previously iterated all 4 suffixes regardless of visibility,
    // so for that mode we always feed the full 4-tuple set to preserve behaviour.
    const hasEquityColumns = this.hasEquityColumns();
    const consolidationTypes: Array<'Consolidated' | 'Separate'> = hasEquityColumns
      ? this.showConsolidatedColumns
        ? ['Consolidated', 'Separate']
        : ['Separate']
      : ['Consolidated', 'Separate'];
    const periodTypes: Array<'Current' | 'Previous'> = hasEquityColumns
      ? this.hasPreviousYear
        ? ['Current', 'Previous']
        : ['Current']
      : ['Current', 'Previous'];
    const visibleColumns = consolidationTypes.flatMap((ct) =>
      periodTypes.map((pt) => ({
        consolidationType: ct,
        periodType: pt,
        visible: true,
      }))
    );

    const calculatedTotals = this.calculationService.calculateTotals_610000(
      formData,
      xbrlPrefix,
      {
        hasEquityColumns,
        equityColumns: this.schema.equityColumns,
        visibleColumns,
      }
    );

    Object.keys(calculatedTotals).forEach((controlName) => {
      this.statementOfChangesInEquityForm
        .get(controlName)
        ?.setValue(calculatedTotals[controlName], { emitEvent: false });
    });

    // Register calculated keys so isSheetModified() skips them
    this.calculatedTotalKeys = Object.keys(calculatedTotals);
    this.stateService.registerCalculatedKeys(
      SHEET_KEYS.statementOfChangesInEquity,
      Object.keys(calculatedTotals)
    );
  }

  /**
   * Centralized validation entry point — runs both same-sheet and cross-sheet.
   * Called during initialization and from loadSavedState.
   */
  private async runAllValidations(): Promise<void> {
    this.validateSameSheet();
    await this.validateCrossSheet();
  }

  /**
   * SAME-SHEET validation — safe to run live during edit.
   * Clears + rebuilds only the same-sheet partition; leaves frozen cross-sheet
   * errors (those in crossSheetErrorQnames) untouched.
   * Also runs the same-sheet sub-checks extracted from the previously-bundled
   * runEqualityValidations/updateEquityBeginningEndCellErrors.
   */
  private validateSameSheet(): void {
    // Clear same-sheet entries only — preserve frozen cross-sheet errors
    for (const key of Array.from(this.validationErrors.keys())) {
      if (!this.crossSheetErrorQnames.has(key)) {
        this.validationErrors.delete(key);
      }
    }

    // Same-sheet validation methods
    this.validateAgainstApiData();
    this.validateRequiredFields();
    this.validateColumnSum();
    this.validateEquityTotal();
    this.runSameSheetEqualityChecks();
    this.updateSameSheetBeginningEndErrors();

    // Finalize
    this.validationErrors = new Map(this.validationErrors);
    this.isSyncValidationComplete.set(true);
    this.rebuildValidationCaches();
    this.bumpView();
  }

  /**
   * CROSS-SHEET validation — deferred to Tick and cross-sheet listeners.
   * Clears + rebuilds only the cross-sheet partition (crossSheetErrorQnames).
   */
  private async validateCrossSheet(): Promise<void> {
    // Remove previous cross-sheet entries from validationErrors
    for (const qname of this.crossSheetErrorQnames) {
      // Preserve same-sheet errors (API mismatch, column sum, equity total, required)
      const existing = this.validationErrors.get(qname) || [];
      const preserved = existing.filter(msg =>
        msg.includes(EXTRACTED_SUM_MARKER) ||
        msg.includes('should equal to the sum of') ||
        msg.includes('Total should equal to the sum of') ||
        msg.includes(MANDATORY_MESSAGE)
      );
      if (preserved.length > 0) {
        this.validationErrors.set(qname, preserved);
      } else {
        this.validationErrors.delete(qname);
      }
    }
    this.crossSheetErrorQnames.clear();

    // Run cross-sheet sub-checks (from previously-bundled runEqualityValidations)
    await this.runCrossSheetEqualityChecks();

    this.rebuildValidationCaches();
    // OnPush re-render trigger — MUST mirror validateSameSheet()'s trailing
    // bumpView(). The cross-sheet (by-year SFP↔SOCIE) error lands ASYNCHRONOUSLY
    // (runCrossSheetEqualityChecks awaits the SFP schema load), so by the time it
    // is written into validationErrors / the row caches above, the synchronous
    // render that validateSameSheet() bumped has already painted WITHOUT it.
    // Under OnPush the row templates only re-read the caches when the bound
    // `viewVersion()` signal changes, so without this bump the freshly-computed
    // cross-sheet error stays invisible until some OTHER trigger marks the
    // component for check. On the Tick path that trigger is the click event
    // itself (which marks OnPush for check), which masked this gap; but on a COLD
    // initial load the validation runs from a setTimeout with no user event, so
    // the SOCIE editor never repainted and the error showed only on the listing
    // (sheet status X, computed via evaluateSheetStatus → updateSheetLevelStatus,
    // which does NOT bump the view) and on SOFP — never in the SOCIE editor.
    this.bumpView();
  }

  /**
   * Same-sheet sub-checks extracted from runEqualityValidations:
   *   - validateConsecutiveYearEquity (within-role19 consecutive year)
   *   - validateConsecutiveYearEquityTotal (total column)
   *
   * These validators only need:
   *   1. Role-19's own form values (compared column-by-column within the sheet)
   *   2. DEI date fields (filingPrerequisites / filingInfo) to check year-consecutiveness
   *
   * They do NOT read r8/r12/r15. Building from exportAllData() + recomputeReferencedSheets()
   * here was a cross-sheet read on the live per-keystroke path — fixed by constructing
   * the bag directly from the three sheets the validators actually consume.
   */
  private runSameSheetEqualityChecks(): void {
    if (!this.schema) return;

    const isMFRS = this.schema.taxonomy === 'FS-MFRS';
    const currentTaxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';

    // Build a minimal bag: role19 own values + DEI sheets (filingPrerequisites + filingInfo).
    // validateConsecutiveYearEquity / validateConsecutiveYearEquityTotal read ONLY these three
    // slices — no r8/r12/r15 needed, so no exportAllData() or recomputeReferencedSheets().
    const formValuesWithAllSheets = {
      [SHEET_KEYS.statementOfChangesInEquity]: this.statementOfChangesInEquityForm.getRawValue(),
      [SHEET_KEYS.filingPrerequisites]: this.stateService.getSheetData(SHEET_KEYS.filingPrerequisites as any),
      [SHEET_KEYS.filingInfo]: this.stateService.getSheetData(SHEET_KEYS.filingInfo as any),
    };

    // SAME-SHEET: consecutive year equity (column-by-column, excluding total)
    const consecutiveYearErrors = this.equityReconciliation.validateConsecutiveYearEquity(
      this.schema,
      formValuesWithAllSheets,
      currentTaxonomy,
      SHEET_KEYS.statementOfChangesInEquity
    );

    // SAME-SHEET: consecutive year equity total (total column only)
    const consecutiveYearTotalErrors = this.equityReconciliation.validateConsecutiveYearEquityTotal(
      this.schema,
      formValuesWithAllSheets,
      currentTaxonomy,
      SHEET_KEYS.statementOfChangesInEquity
    );

    // Merge the two same-sheet roll-forward results into the dedicated
    // sameSheetEqualityErrors map (per-column + Total column).
    const mergedSameSheet = { ...consecutiveYearErrors };
    Object.keys(consecutiveYearTotalErrors).forEach(qname => {
      if (mergedSameSheet[qname]) {
        mergedSameSheet[qname] = [...mergedSameSheet[qname], ...consecutiveYearTotalErrors[qname]];
      } else {
        mergedSameSheet[qname] = consecutiveYearTotalErrors[qname];
      }
    });

    // Rebuild every affected qname from ALL sources (same-sheet roll-forward +
    // frozen cross-sheet by-year). The affected set is the union of the qnames
    // that previously carried a same-sheet equality error and the ones that do
    // now, so a resolved roll-forward error is cleared while a frozen cross-sheet
    // by-year error on the SAME key (Equity:End) is preserved — never clobbered.
    const affected = new Set<string>([
      ...this.sameSheetEqualityErrors.keys(),
      ...Object.keys(mergedSameSheet),
    ]);
    this.sameSheetEqualityErrors = new Map(Object.entries(mergedSameSheet));
    affected.forEach(qname => this.rebuildEqualityMessages(qname));
  }

  /**
   * Rebuild validationErrors[qname] deterministically from the four authoritative
   * sources so every same-sheet message and the cross-sheet message coexist on a
   * shared key (Equity:Beginning / Equity:End) instead of one stomping the other:
   *   1. base        — non-equality same-sheet entries already on the key that we
   *                    always keep (extracted-sum suggested change, sum rollups,
   *                    mandatory). Stale equality messages are NOT in this set, so
   *                    they drop out and are re-sourced fresh below.
   *   2. begin/end   — sameSheetBeginningEndErrors (the UNCONDITIONAL [#f3]-vs-[#f22]
   *                    cell check; spec 19a rule 5's canonical same-sheet source).
   *   3. roll-forward— sameSheetEqualityErrors (the consecutive-year-GATED check). It is
   *                    a SUBSET of (2), so any entry targeting the same table+column as a
   *                    begin/end entry is dropped — otherwise the display (which has no
   *                    dedup) would show two near-identical messages on the same cell.
   *   4. cross-sheet — equalityValidationErrors (by-year vs SOFP + schema equality).
   * Deletes the key when nothing remains.
   */
  private rebuildEqualityMessages(qname: string): void {
    const existing = this.validationErrors.get(qname) || [];
    const base = existing.filter(msg =>
      msg.includes(EXTRACTED_SUM_MARKER) ||
      msg.includes('should equal to the sum of') ||
      msg.includes('Total should equal to the sum of') ||
      msg.includes(MANDATORY_MESSAGE)
    );
    const beginEndEq = (this.sameSheetBeginningEndErrors.get(qname) || []).map(equalityError => equalityError.message);
    const beginEndCells = new Set(beginEndEq.map(message => this.equalityMessageCellKey(message)));
    const rollForwardEq = (this.sameSheetEqualityErrors.get(qname) || [])
      .map(equalityError => equalityError.message)
      .filter(message => !beginEndCells.has(this.equalityMessageCellKey(message)));
    const crossEq = (this.equalityValidationErrors.get(qname) || []).map(equalityError => equalityError.message);
    const next = [...base, ...beginEndEq, ...rollForwardEq, ...crossEq];
    if (next.length > 0) {
      this.validationErrors.set(qname, next);
    } else {
      this.validationErrors.delete(qname);
    }
  }

  /**
   * Stable "<table>: <column>" identity for an equality message, taken from its
   * "<table>: <column>: Mismatch…" prefix (e.g. "Consolidated Current: Total: …" →
   * "consolidated current: total"). Both the begin/end cell check and the gated
   * consecutive-year roll-forward emit that prefix, so the canonical begin/end
   * message can dedupe the redundant roll-forward one targeting the same cell.
   */
  private equalityMessageCellKey(message: string): string {
    return message.split(':').slice(0, 2).join(':').trim().toLowerCase();
  }

  /**
   * Same-sheet sub-check from updateEquityBeginningEndCellErrors: the UNCONDITIONAL
   * [#f3]-vs-[#f22] cell check (spec 19a rule 5) — compare Equity:Beginning
   * (Current Year) vs Equity:End (Previous Year) per equity column. Gated on
   * `hasPreviousYear` only, NOT on consecutive years. Does NOT read role8 SoFP data.
   *
   * Records mismatches into BOTH the red-border flag map (`equityBeginningEndErrors`)
   * AND the dedicated message map (`sameSheetBeginningEndErrors`), then re-sources the
   * display via `rebuildEqualityMessages` so the message coexists with any cross-sheet
   * error on the same key (it used to be a direct `validationErrors` write that the
   * cross-sheet preserve-filter permanently dropped → cell showed only a red border).
   */
  private updateSameSheetBeginningEndErrors(): void {
    this.equityBeginningEndErrors.clear();

    // Qnames that previously carried a begin/end message — rebuild them too so a
    // resolved mismatch clears (the new map is rebuilt fresh below).
    const previousKeys = new Set(this.sameSheetBeginningEndErrors.keys());
    this.sameSheetBeginningEndErrors = new Map<string, EqualityValidationError[]>();

    const isMFRS = this.crossRefService.isMFRS();
    const taxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';
    const beginningQname = isMFRS ? 'ifrs-full:Equity:Beginning' : 'ifrs-smes:Equity:Beginning';
    const endQname = isMFRS ? 'ifrs-full:Equity:End' : 'ifrs-smes:Equity:End';

    // Accumulate the cell-check message onto a qname's slice of the dedicated map.
    const record = (qname: string, message: string, compareField: string, difference: number): void => {
      const list = this.sameSheetBeginningEndErrors.get(qname) ?? [];
      if (!list.some(existingError => existingError.message === message)) {
        list.push({
          severity: 'ERROR',
          message,
          type: 'equality',
          compareWith: { sheet: SHEET_KEYS.statementOfChangesInEquity, field: compareField },
          difference,
          tolerance: 0,
        });
        this.sameSheetBeginningEndErrors.set(qname, list);
      }
    };

    if (this.hasPreviousYear && this.schema) {
      const consolidationTypes = this.showConsolidatedColumns
        ? ['Consolidated', 'Separate']
        : ['Separate'];

      if (this.hasEquityColumns() && this.schema.equityColumns) {
        for (const consolidationType of consolidationTypes) {
          for (const column of this.schema.equityColumns) {
            const beginningControlName = this.getEquityColumnControlName(beginningQname, column.qname, consolidationType, 'Current');
            const endControlName = this.getEquityColumnControlName(endQname, column.qname, consolidationType, 'Previous');

            const beginningValue = Math.round((parseFloat(this.statementOfChangesInEquityForm.get(beginningControlName)?.value) || 0) * 100) / 100;
            const endValue = Math.round((parseFloat(this.statementOfChangesInEquityForm.get(endControlName)?.value) || 0) * 100) / 100;

            if (beginningValue === 0 && endValue === 0) continue;

            if (beginningValue !== endValue) {
              const colLabel = column.label || column.qname;
              const difference = Math.round((beginningValue - endValue) * 100) / 100;

              this.equityBeginningEndErrors.set(beginningControlName, 'mismatch');
              this.equityBeginningEndErrors.set(endControlName, 'mismatch');

              record(beginningQname, this.equalityValidation.formatFieldMismatch({
                columnLabel: `${consolidationType} Current`,
                qualifier: colLabel,
                fieldQname: 'Equity at end of period (Previous year)',
                fieldSheetKey: SHEET_KEYS.statementOfChangesInEquity,
                taxonomy,
                sheetLabelOverride: 'Statement of Changes in Equity',
              }), endQname, difference);

              record(endQname, this.equalityValidation.formatFieldMismatch({
                columnLabel: `${consolidationType} Previous`,
                qualifier: colLabel,
                fieldQname: 'Equity at beginning of period (Current year)',
                fieldSheetKey: SHEET_KEYS.statementOfChangesInEquity,
                taxonomy,
                sheetLabelOverride: 'Statement of Changes in Equity',
              }), beginningQname, difference);
            }
          }
        }
      } else {
        // Dual column mode
        for (const consolidationType of consolidationTypes) {
          const beginningControlName = beginningQname + `[${consolidationType}][CurrentYear]`;
          const endControlName = endQname + `[${consolidationType}][PreviousYear]`;

          const beginningValue = Math.round((parseFloat(this.statementOfChangesInEquityForm.get(beginningControlName)?.value) || 0) * 100) / 100;
          const endValue = Math.round((parseFloat(this.statementOfChangesInEquityForm.get(endControlName)?.value) || 0) * 100) / 100;

          if (beginningValue === 0 && endValue === 0) continue;

          if (beginningValue !== endValue) {
            const difference = Math.round((beginningValue - endValue) * 100) / 100;

            this.equityBeginningEndErrors.set(beginningControlName, 'mismatch');
            this.equityBeginningEndErrors.set(endControlName, 'mismatch');

            record(beginningQname, this.equalityValidation.formatFieldMismatch({
              columnLabel: `${consolidationType} Current`,
              fieldQname: 'Equity at end of period (Previous year)',
              fieldSheetKey: SHEET_KEYS.statementOfChangesInEquity,
              taxonomy,
              sheetLabelOverride: 'Statement of Changes in Equity',
            }), endQname, difference);

            record(endQname, this.equalityValidation.formatFieldMismatch({
              columnLabel: `${consolidationType} Previous`,
              fieldQname: 'Equity at beginning of period (Current year)',
              fieldSheetKey: SHEET_KEYS.statementOfChangesInEquity,
              taxonomy,
              sheetLabelOverride: 'Statement of Changes in Equity',
            }), beginningQname, difference);
          }
        }
      }
    }

    // Re-source the display for every qname that previously OR now carries a begin/end
    // message, so resolved mismatches clear and new ones surface (clobber-proof).
    const affected = new Set<string>([...previousKeys, ...this.sameSheetBeginningEndErrors.keys()]);
    affected.forEach(qname => this.rebuildEqualityMessages(qname));
  }

  /**
   * Cross-sheet sub-checks extracted from runEqualityValidations:
   *   - validateAllEqualityRules (reads r8/r12/r15 via exportAllData + recompute)
   *   - validateCrossSheetEquityByYear (async — loads SFP schema)
   * Records every qname written into crossSheetErrorQnames.
   */
  private async runCrossSheetEqualityChecks(): Promise<void> {
    if (!this.schema) return;

    const currentGeneration = ++this.equalityValidationGeneration;

    const isMFRS = this.schema.taxonomy === 'FS-MFRS';
    const currentTaxonomy: 'MFRS' | 'MPERS' = isMFRS ? 'MFRS' : 'MPERS';

    // Roster + schema provider come from the SHARED role-19 rules seam, so these
    // cross-sheet messages are computed over the SAME recomputed operands as the
    // pure validator that publishes this sheet's verdict. Deriving them here
    // instead is what produced a red "does not match Total equity" message under
    // a green `Success *` chip: the local roster came from
    // `referencedSheetKeysFromSchema`, which sees `validation.equality` rules
    // only, so role 8 (reached by `crossSheetEquityByYear`) was never recomputed
    // and this compared against role 8's STALE persisted `Equity`. The old
    // `getCachedSchema` provider could not have fixed it either —
    // `preloadReferencedSchemas` populates that cache from the same
    // equality-only walk, so role 8's schema was absent and its role-9
    // inheritance would have been skipped even once the key was added. See
    // `role19SchemaForReferencedSheet`.
    const allSheetData = this.stateService.exportAllData();
    const formValuesWithAllSheets = recomputeReferencedSheets(
      { ...allSheetData, [SHEET_KEYS.statementOfChangesInEquity]: this.statementOfChangesInEquityForm.getRawValue() },
      role19ReferencedSheetKeys(this.schema),
      currentTaxonomy,
      (key) => role19SchemaForReferencedSheet(key, currentTaxonomy)
    );

    // CROSS: validateAllEqualityRules (reads cross-sheet equality rules from schema)
    const crossErrors = this.equalityValidation.validateAllEqualityRules(
      this.schema,
      formValuesWithAllSheets,
      currentTaxonomy,
      SHEET_KEYS.statementOfChangesInEquity
    );

    // Clean up stale equality errors. equalityValidationErrors is the
    // authoritative cross-sheet source; rebuildEqualityMessages re-sources BOTH
    // partitions from their maps, so a same-sheet roll-forward error on a shared
    // key (Equity:End) is preserved rather than dropped when the cross-sheet
    // partition rebuilds.
    const previousEqualityQnames = new Set(this.equalityValidationErrors.keys());
    this.equalityValidationErrors = new Map(Object.entries(crossErrors));

    previousEqualityQnames.forEach(qname => {
      if (!crossErrors[qname]) {
        this.crossSheetErrorQnames.delete(qname);
        this.rebuildEqualityMessages(qname);
      }
    });

    Object.keys(crossErrors).forEach(qname => {
      this.crossSheetErrorQnames.add(qname);
      this.rebuildEqualityMessages(qname);
    });

    // CROSS: validateCrossSheetEquityByYear (async)
    const sfpSheetKey = '210000';
    return this.formSchemaService.loadSchema(sfpSheetKey, currentTaxonomy).then((sfpSchema: any) => {
      if (currentGeneration !== this.equalityValidationGeneration) return;

      if (sfpSchema) {
        const crossSheetErrors = this.equityReconciliation.validateCrossSheetEquityByYear(
          { sfp: sfpSchema, socie: this.schema },
          formValuesWithAllSheets,
          currentTaxonomy
        );

        // Merge cross-sheet equity errors. The reconciliation now covers both
        // Current Year (ungated) and Previous Year (consecutive-gated) — see
        // EquityReconciliationService.validateCrossSheetEquityByYear.
        const mergedErrors = { ...crossErrors };
        Object.keys(crossSheetErrors).forEach(qname => {
          if (mergedErrors[qname]) {
            mergedErrors[qname] = [...mergedErrors[qname], ...crossSheetErrors[qname]];
          } else {
            mergedErrors[qname] = crossSheetErrors[qname];
          }
        });

        this.equalityValidationErrors = new Map(Object.entries(mergedErrors));

        Object.keys(crossSheetErrors).forEach(qname => {
          this.crossSheetErrorQnames.add(qname);
        });

        // Rebuild every cross-sheet qname (schema equality + by-year) AND any
        // qname that previously carried an equality error, so the freshly merged
        // cross-sheet messages and any same-sheet roll-forward message on the same
        // key coexist. rebuildEqualityMessages sources both partitions from their
        // maps — never letting one stomp the other.
        new Set<string>([...Object.keys(mergedErrors), ...previousEqualityQnames]).forEach(qname =>
          this.rebuildEqualityMessages(qname)
        );
      } else {
        console.error('Failed to load SFP schema for cross-sheet validation');
      }
    }).catch((err: any) => {
      console.error('Error loading SFP schema:', err);
    });
  }

  /**
   * Validate calculated totals against baseline data from API
   * Checks both Y-axis (vertical totals within columns) and X-axis (horizontal totals across columns)
   */
  private validateAgainstApiData(): void {
    // Source the extracted-sum reference from the authoritative N8N snapshot
    // (FilingFormStateService, populated by the container) — not a per-sheet capture.
    const apiData = this.mappingStatus.getSnapshotValues(SHEET_KEYS.statementOfChangesInEquity);

    // Skip validation if no snapshot yet (pre-rollout backend, or snapshot not loaded).
    if (!apiData || Object.keys(apiData).length === 0) {
      return;
    }

    // Only validate calculated/total fields
    const calculatedControls = this.schema?.controls.filter(
      (control) => this.isCalculatedField(control) && !this.isSectionHeader(control)
    ) || [];
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(
    //   `🔍 Validation - Checking ${calculatedControls.length} calculated fields (Role 19)`
    // );

    calculatedControls.forEach((control) => {
      const errors: string[] = [];

      if (this.hasEquityColumns() && this.schema?.equityColumns) {
        // Equity columns mode: Check each equity column across all 4 tables
        const consolidationTypes = this.showConsolidatedColumns ? ['Consolidated', 'Separate'] : ['Separate'];
        const periodTypes = this.hasPreviousYear ? ['Current', 'Previous'] : ['Current'];

        consolidationTypes.forEach(consolidationType => {
          periodTypes.forEach(periodType => {
            this.schema!.equityColumns!.forEach(column => {
              const controlName = this.getEquityColumnControlName(control.qname, column.qname, consolidationType, periodType);
              const formControl = this.statementOfChangesInEquityForm.get(controlName);

              if (!formControl) return;

              // Build column label for error messages
              const columnLabel = `${column.label} [${consolidationType} ${periodType}]`;

              const calculatedValue = formControl.value;
              const baselineValue = apiData[controlName];

              // Convert to numbers for comparison
              const calculatedNum = calculatedValue ? Math.round(parseFloat(calculatedValue) * 100) / 100 : 0;
              const baselineNum =
                baselineValue !== undefined && baselineValue !== null
                  ? Math.round(parseFloat(String(baselineValue)) * 100) / 100
                  : null;

              // Debug Log (Only enable when required to debug, do not allow this to land into prod)
              // if (baselineNum === null && calculatedNum !== 0) {
              //   console.log(
              //     `⚠️ No baseline for ${control.label} [${columnLabel}]`,
              //     {
              //       controlName,
              //       calculatedNum,
              //       baselineValue,
              //       hasBaseline: baselineNum !== null
              //     }
              //   );
              // }

              // Check against baseline (API extracted value)
              const baselineMismatch =
                baselineNum !== null && calculatedNum !== baselineNum;

              // Debug Log (Only enable when required to debug, do not allow this to land into prod)
              // if (baselineMismatch) {
              //   console.log(
              //     `🔍 Mismatch detected for ${control.label} [${columnLabel}]`,
              //     {
              //       calculatedNum,
              //       baselineNum,
              //       diff: calculatedNum - baselineNum,
              //     }
              //   );
              // }

              // Show error if calculated doesn't match baseline (extracted from API)
              if (baselineMismatch) {
                errors.push(
                  `${this.crossRefService.generateMismatchError(
                    baselineNum,
                    calculatedNum,
                    columnLabel
                  )}`
                );
              }
            });
          });
        });
      } else {
        // Dual column mode: Check all 4 columns (Phase 3: bracket format)
        const suffixes = [
          { suffix: '[Consolidated][CurrentYear]', label: 'Consolidated Current' },
          { suffix: '[Consolidated][PreviousYear]', label: 'Consolidated Previous' },
          { suffix: '[Separate][CurrentYear]', label: 'Separate Current' },
          { suffix: '[Separate][PreviousYear]', label: 'Separate Previous' }
        ];

        suffixes.forEach(({ suffix, label }) => {
          const controlName = control.qname + suffix;
          const formControl = this.statementOfChangesInEquityForm.get(controlName);

          if (!formControl) return;

          const calculatedValue = formControl.value;
          const baselineValue = apiData[controlName];

          // Convert to numbers for comparison
          const calculatedNum = calculatedValue ? Math.round(parseFloat(calculatedValue) * 100) / 100 : 0;
          const baselineNum =
            baselineValue !== undefined && baselineValue !== null
              ? Math.round(parseFloat(String(baselineValue)) * 100) / 100
              : null;

          // Check against baseline
          const baselineMismatch =
            baselineNum !== null && calculatedNum !== baselineNum;

          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // if (baselineMismatch) {
          //   console.log(
          //     `🔍 Mismatch detected for ${control.label} [${label}]`,
          //     {
          //       calculatedNum,
          //       baselineNum,
          //       diff: calculatedNum - baselineNum,
          //     }
          //   );
          // }

          if (baselineMismatch) {
            errors.push(
              `${this.crossRefService.generateMismatchError(
                baselineNum,
                calculatedNum,
                label
              )}`
            );
          }
        });
      }

      // Preserve non-API errors, only manage API mismatch errors
      const existingErrors = this.validationErrors.get(control.qname) || [];
      const nonApiErrors = existingErrors.filter(error =>
        !error.includes(EXTRACTED_SUM_MARKER)
      );
      if (errors.length > 0) {
        this.validationErrors.set(control.qname, [...nonApiErrors, ...errors]);
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(
        //   `⚠️ Validation errors for ${control.label} (${control.qname}):`,
        //   errors
        // );
      } else if (nonApiErrors.length > 0) {
        this.validationErrors.set(control.qname, nonApiErrors);
      } else {
        this.validationErrors.delete(control.qname);
      }
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(
    //   `🔍 Validation complete - ${this.validationErrors.size} fields with errors (Role 19)`
    // );

    // if (this.validationErrors.size > 0) {
    //   console.log(
    //     '🔍 Fields with errors:',
    //     Array.from(this.validationErrors.keys())
    //   );
    // }
  }

  /**
   * Check if a field has equality validation errors
   */
  hasEqualityError(qname: string): boolean {
    return this.equalityValidationErrors.has(qname) &&
      this.equalityValidationErrors.get(qname)!.length > 0;
  }

  /**
   * Get equality validation error message for a field
   */
  getEqualityErrorMessage(qname: string): string {
    const errors = this.equalityValidationErrors.get(qname);
    if (!errors || errors.length === 0) return '';

    return errors.map(err => err.message).join('; ');
  }

  /**
   * Calculate horizontal totals across equity columns for a specific row
   * DISABLED: Based on mtools behavior, auto-calculation is not performed
   */
  private calculateEquityRowTotals(rowQname: string): void {
    // No automatic calculations
    /*
    if (!this.schema?.equityColumns) return;

    const isMFRS = this.crossRefService.isMFRS();
    const totalColumnQname = isMFRS ? 'ifrs-full:EquityMember' : 'ifrs-smes:EquityMember';
    const equityAttributableQname = isMFRS ? 'ifrs-full:EquityAttributableToOwnersOfParentMember' : 'ifrs-smes:EquityAttributableToOwnersOfParentMember';
    const reservesQname = isMFRS ? 'ifrs-full:OtherReservesMember' : 'ifrs-smes:OtherReservesMember';
    const nonDistributableQname = 'ssmt:NonDistributableReservesMember';
    const issuedCapitalQname = isMFRS ? 'ifrs-full:IssuedCapitalMember' : 'ifrs-smes:IssuedCapitalMember';
    const retainedEarningsQname = isMFRS ? 'ifrs-full:RetainedEarningsMember' : 'ifrs-smes:RetainedEarningsMember';
    const treasurySharesQname = isMFRS ? 'ifrs-full:TreasurySharesMember' : 'ifrs-smes:TreasurySharesMember';
    const capitalReserveQname = isMFRS ? 'ifrs-full:CapitalReserveMember' : 'ssmt-mpers:CapitalReserveMember';
    const foreignCurrencyQname = isMFRS ? 'ssmt-mfrs:ForeignCurrencyTranslationReserveMember' : 'ssmt-mpers:ForeignCurrencyTranslationReserveMember';
    const revaluationQname = isMFRS ? 'ifrs-full:RevaluationSurplusMember' : 'ifrs-smes:RevaluationSurplusMember';
    const otherNonDistributableQname = isMFRS ? 'ssmt-mfrs:OtherNondistributableReserveMember' : 'ssmt-mpers:OtherNondistributableReserveMember';
    const otherDistributableQname = isMFRS ? 'ssmt-mfrs:OtherDistributableReserveMember' : 'ssmt-mpers:OtherDistributableReserveMember';
    const otherComponentsQname = isMFRS ? 'ssmt-mfrs:OtherComponentsOfEquityMember' : 'ssmt-mpers:OtherComponentsOfEquityMember';
    const nonControllingQname = isMFRS ? 'ifrs-full:NoncontrollingInterestsMember' : 'ifrs-smes:NoncontrollingInterestsMember';

    // 1. Calculate Sub-total of non-distributable reserves = Sum of component reserves
    const capitalReserve = this.getEquityColumnValue(rowQname, capitalReserveQname);
    const foreignCurrency = this.getEquityColumnValue(rowQname, foreignCurrencyQname);
    const revaluation = this.getEquityColumnValue(rowQname, revaluationQname);
    const otherNonDistributable = this.getEquityColumnValue(rowQname, otherNonDistributableQname);
    const nonDistributableTotal = capitalReserve + foreignCurrency + revaluation + otherNonDistributable;

    this.statementOfChangesInEquityForm.get(this.getEquityColumnControlName(rowQname, nonDistributableQname))
      ?.setValue(nonDistributableTotal, { emitEvent: false });

    // 2. Calculate Reserves = Sub-total non-distributable + Other distributable
    const otherDistributable = this.getEquityColumnValue(rowQname, otherDistributableQname);
    const reservesTotal = nonDistributableTotal + otherDistributable;

    this.statementOfChangesInEquityForm.get(this.getEquityColumnControlName(rowQname, reservesQname))
      ?.setValue(reservesTotal, { emitEvent: false });

    // 3. Calculate Equity attributable to owners = Issued capital + Retained earnings + Treasury shares + Reserves
    const issuedCapital = this.getEquityColumnValue(rowQname, issuedCapitalQname);
    const retainedEarnings = this.getEquityColumnValue(rowQname, retainedEarningsQname);
    const treasuryShares = this.getEquityColumnValue(rowQname, treasurySharesQname);
    const equityAttributable = issuedCapital + retainedEarnings + treasuryShares + reservesTotal;

    this.statementOfChangesInEquityForm.get(this.getEquityColumnControlName(rowQname, equityAttributableQname))
      ?.setValue(equityAttributable, { emitEvent: false });

    // 4. Calculate Total = Equity attributable + Other components + Non-controlling interests
    const otherComponents = this.getEquityColumnValue(rowQname, otherComponentsQname);
    const nonControlling = this.getEquityColumnValue(rowQname, nonControllingQname);
    const total = equityAttributable + otherComponents + nonControlling;

    this.statementOfChangesInEquityForm.get(this.getEquityColumnControlName(rowQname, totalColumnQname))
      ?.setValue(total, { emitEvent: false });
    */
  }

  /**
   * Get form control name for equity column mode (Phase 3)
   * Format: {rowQname}:{columnQname}[{consolidationType}][{periodType}Year]
   * For 4 separate tables based on consolidation type and period
   */
  getEquityColumnControlName(rowQname: string, columnQname: string, consolidationType?: string, periodType?: string): string {
    // If no consolidationType/periodType provided, use simplified format (for backward compatibility)
    if (!consolidationType || !periodType) {
      return `${rowQname}:${columnQname}`;
    }

    // Phase 3: Standard bracket format with CurrentYear/PreviousYear
    const period = periodType === 'Current' ? 'CurrentYear' : 'PreviousYear';
    return `${rowQname}:${columnQname}[${consolidationType}][${period}]`;
  }

  /**
   * Check if schema uses equity columns
   */
  hasEquityColumns(): boolean {
    return this.schema?.equityColumns !== undefined && this.schema.equityColumns.length > 0;
  }

  /**
   * Is the Ctrl+Alt+T column-compression mode currently affecting this grid?
   *
   * True only when: not production, the paging toggle is ON, and the schema
   * actually has equity columns to compress. The original-grid template
   * branch (the only place these tables render) is already mutually exclusive
   * with the Ctrl+Alt+D debug layout, and the paging service ignores its
   * chords while that layout is active — so compression is operational on the
   * original 4-table grid only. Drives both `visibleEquityColumns()` and the
   * `.column-compressed` CSS class that widens the Error Description column.
   */
  isColumnCompressionActive(): boolean {
    return (
      !this.envService.isProduction() &&
      this.debugColumnPaging.enabled() &&
      this.hasEquityColumns()
    );
  }

  /**
   * The equity columns to actually render, in order.
   *
   * Normal / production: every column (identical to iterating
   * `schema.equityColumns` directly, so behaviour is unchanged).
   *
   * Ctrl+Alt+T compression ON: only the current page window of up to
   * `pageSize` (3) columns — `schema.equityColumns.slice(start, start + 3)`.
   * The last page shows the 1–2 column remainder because the slice is bounded
   * by the array length. The fixed columns (field name, Actions, Mapping
   * Status, Error Description) are rendered outside this list and are never
   * paged. Reads the paging signals, so toggling/paging re-renders the grid
   * under OnPush.
   */
  visibleEquityColumns(): EquityColumn[] {
    const all = this.schema?.equityColumns ?? [];
    if (!this.isColumnCompressionActive()) return all;
    const start = this.debugColumnPaging.windowStart();
    return all.slice(start, start + this.debugColumnPaging.pageSize);
  }

  /**
   * Load saved state from service
   */
  private loadSavedState(): void {
    this.stateService.markSheetInitializing(SHEET_KEYS.statementOfChangesInEquity);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.statementOfChangesInEquity);
    if (savedData && Object.keys(savedData).length > 0) {
      // Debug: Log keys for subtotal columns before patchValue
      const subtotalColumns = ['NonDistributableReserves', 'OtherReserves', 'EquityAttributableToOwners'];
      const relevantKeys = Object.keys(savedData).filter(key =>
        subtotalColumns.some(col => key.includes(col))
      );
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('🔍 [loadSavedState] Subtotal column keys in savedData:', relevantKeys);
      // relevantKeys.forEach(key => {
      //   console.log(`   ${key} = ${savedData[key]}`);
      // });

      this.statementOfChangesInEquityForm.patchValue(savedData, { emitEvent: false });

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('Role-19 state loaded from localStorage');

      // Recalculate vertical totals and run all validations after loading
      this.initTimeoutIds.push(setTimeout(async () => {
        // Re-enter hydration window: this deferred init write fires AFTER the
        // outer begin/endHydration bracket in loadSchema's .then() has closed.
        // Without this, the updateSheetData below would emit a [notify-entry]
        // for 'modifiedState' even though no user action occurred.
        this.stateService.beginHydration();
        try {
          this.calculateTotals();
          // Save calculated values to state so other sheets (e.g., Role 15) can read
          // the latest calculated totals for cross-sheet validation.
          // calculateTotals() uses emitEvent: false, so setupStatePersistence doesn't fire.
          this.stateService.updateSheetData(
            SHEET_KEYS.statementOfChangesInEquity,
            this.statementOfChangesInEquityForm.getRawValue(),
            true,
            true // skipEditedMark — this is initialization, not a user edit
          );

          await this.runAllValidations();
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.statementOfChangesInEquity);
        } finally {
          this.stateService.endHydration();
        }
      }, 100));
    } else {
      // Still calculate totals so calculated fields show 0.00 instead of blank
      // Also run equality validations to detect cross-sheet mismatches (e.g., ProfitLoss vs Role 12)
      this.initTimeoutIds.push(setTimeout(async () => {
        // Re-enter hydration window: same rationale as the savedData branch above.
        this.stateService.beginHydration();
        try {
          this.calculateTotals();
          // Save calculated values to state
          this.stateService.updateSheetData(
            SHEET_KEYS.statementOfChangesInEquity,
            this.statementOfChangesInEquityForm.getRawValue(),
            true,
            true
          );

          await this.runAllValidations();
          this.evaluateSheetStatus();
          this.stateService.markSheetInitialized(SHEET_KEYS.statementOfChangesInEquity);
        } finally {
          this.stateService.endHydration();
        }
      }, 100));
    }
  }

  /**
   * Setup cross-sheet reactivity (modifiedState$ and remoteUpdateApplied$ subs).
   * Auto-save and live-edit calculation/validation/status are handled by setupEditPipeline().
   */
  private setupStatePersistence(): void {
      // ----------------------------------
      // Listen for Role 8 / Role 12 / Role 15 data changes only (not SOCIE's own saves).
      //
      // Three-layer guard against spurious init-time firings:
      //   1. filter(!isSheetInitializing) — drops emissions produced by SOCIE's own
      //      loadSavedState → updateSheetData chain while the sheet is still initializing.
      //   2. distinctUntilChanged on the r8/r12/r15 hash — SOCIE's self-updates leave
      //      r8/r12/r15 slice references untouched (shallow spread in updateSheetData),
      //      so the hash is byte-identical and gets filtered out.
      //   3. skip(1) AFTER distinctUntilChanged — drops the BehaviorSubject's first
      //      replayed post-init distinct value (distinctUntilChanged always lets its
      //      first-ever value through because it has no prior value to compare against).
      //
      // Why r8 (SoFP) is in the watched slice:
      //   validateCrossSheetEquityByYear() (via runCrossSheetEqualityChecks)
      //   reads the SoFP slice, so SoCIE's "Equity:End vs SoFP Total Equity"
      //   by-year reconciliation becomes stale whenever role8 changes. Without
      //   r8 in this hash, edits to
      //   SoFP that arrive via SPA navigation (or a sibling tab — see
      //   remoteUpdateApplied$ below) would silently leave SoCIE's red-border
      //   indicators wrong until the user manually re-entered edit mode.
      //
      // Only real changes to Role 8, Role 12 or Role 15 data reach the subscribe block.
      this.stateService.modifiedState$
        .pipe(
          filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfChangesInEquity)),
          map(state => JSON.stringify({
            r0: state.filingPrerequisites,
            r1: state.filingInfo,
            r8: state.statementOfFinancialPosition,
            r12: state.statementOfProfitOrLoss,
            r15: state.statementOfComprehensiveIncome
          })),
          distinctUntilChanged(),
          skip(1),
          takeUntil(this.destroy$)
        )
        .subscribe(async () => {
          await this.validateCrossSheet();
          this.evaluateSheetStatus();
          this.bumpView();
        });
      // ----------------------------------

      // ----------------------------------
      // Cross-tab remote-update re-validation trigger.
      //
      // The modifiedState$ watch above already covers the local case (user edits
      // SoFP/SoPL/SoCI in this tab, navigates back to SoCIE). It ALSO covers the
      // remote case in principle — applyRemoteChange() calls
      // modifiedStateSubject.next(...) with a fresh state reference, which the
      // hash above would observe — BUT only IF the user already happened to be
      // looking at SoCIE when the remote update arrived AND the sheet was past
      // its init guard.
      //
      // This explicit subscription is a safety net for the cross-tab path:
      //   - Tab A edits SoFP, debounced cross-tab notify fires
      //   - Tab B (on SoCIE) receives applyRemoteChange('modifiedState') → next()
      //   - The above hash subscription sees r8 changed and re-validates
      //   - This subscription ALSO fires (redundant by design), but
      //     runAllValidations() is idempotent so the duplicate is harmless and
      //     guarantees re-validation even if the hash subscription is somehow
      //     short-circuited (init guard, schema-not-loaded race, etc.).
      //
      // No write loop concern: runAllValidations() writes sheet-level status
      // via updateSheetLevelStatus(), which does NOT call notifyCrossTab(). And
      // applyRemoteChange has already cleared isApplyingRemote by the time we
      // get here, so any incidental updateSheetData() write WOULD propagate —
      // which is correct behaviour (user-driven validation produces a write).
      this.stateService.remoteUpdateApplied$
        .pipe(
          filter(({ streamKey }) => streamKey === 'modifiedState'),
          filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.statementOfChangesInEquity)),
          filter(() => !!this.schema && !!this.statementOfChangesInEquityForm),
          takeUntil(this.destroy$)
        )
        .subscribe(async ({ sheetKey, changedSheets }) => {
          if (this.envService.isCrossTabSyncDebugEnabled()) {
            // console.log('[CrossTabSync][remote-validation-triggered]', {
            //   receiver: 'role19-statementOfChangesInEquity',
            //   sourceSheetKey: sheetKey,
            // });
          }

          // Own-slice change -> repaint this sheet's fields. Other-slice change ->
          // fall through to the cross-sheet revalidation tail only, as before.
          if (changedSheets.includes(SHEET_KEYS.statementOfChangesInEquity))
            this.adoptRemoteState();

          await this.validateCrossSheet();
          this.evaluateSheetStatus();
          this.bumpView();
        });
      // ----------------------------------
  }

  /**
   * Cross-tab adoption for SOCIE. Rows currently open in edit mode are skipped —
   * the user's in-flight work wins until they tick (local overwrites and
   * broadcasts) or press X (the row lands on the adopted value; see
   * `cancelEditMode`).
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoInlineEditSheet`.
   * `calculateTotals()` here recomputes for DISPLAY only; the sibling already
   * persisted its own recomputed totals.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    const openControlNames = [...this.editingRows.values()]
      .flatMap(snapshot => Object.keys(snapshot ?? {}));

    adoptIntoInlineEditSheet(
      this.statementOfChangesInEquityForm,
      this.stateService.getSheetData(SHEET_KEYS.statementOfChangesInEquity),
      openControlNames
    );

    this.calculateTotals();
    this.validateSameSheet();
  }

  /**
   * Build the edit-mode key for a row.
   * In equity-columns mode, the unit of editing is one specific (consolidation, period)
   * table cell-row, so keys are namespaced by both. In legacy dual-column mode, there is
   * only one editable row per qname (all 4 cells live on a single <tr>), so the bare qname
   * is sufficient.
   */
  private getEditKey(control: FieldControl, consolidationType?: string, periodType?: string): string {
    return (consolidationType && periodType)
      ? `${control.qname}|${consolidationType}|${periodType}`
      : control.qname;
  }

  /**
   * Check if a row is currently in edit mode.
   * Pass (consolidationType, periodType) in equity-columns mode so the lookup is scoped
   * to one of the 4 tables; omitting them falls back to the legacy single-key behaviour
   * used by the dual-column template.
   */
  isRowInEditMode(control: FieldControl, consolidationType?: string, periodType?: string): boolean {
    this.viewVersion();
    return this.editingRows.has(this.getEditKey(control, consolidationType, periodType));
  }

  /**
   * Enter edit mode for a row - store original values, enable controls.
   * Scope is the single (consolidation, period) table the user clicked into, not all 4.
   */
  enterEditMode(control: FieldControl, consolidationType?: string, periodType?: string): void {
    // Don't allow editing before validation is complete
    if (!this.isSyncValidationComplete()) {
      return;
    }
    // Don't allow editing calculated or readonly rows
    if (this.isCalculatedField(control) || control.ui.readonly) {
      return;
    }

    // Capture the committed badge BEFORE entering edit mode (isRowInEditMode is still false here).
    // Use the same key as rowValidationStatusCache / getRowValidationStatus.
    const cacheKey = `${control.qname}::${consolidationType ?? ''}::${periodType ?? ''}`;
    this.frozenRowStatus.set(cacheKey, this.getRowValidationStatus(control, consolidationType, periodType));

    const controlNames = this.collectEditableControlNames(control, consolidationType, periodType);

    // Store original values
    const originalValues: { [key: string]: number | null } = {};
    controlNames.forEach(name => {
      originalValues[name] = this.statementOfChangesInEquityForm.get(name)?.value ?? null;
    });
    this.editingRows.set(this.getEditKey(control, consolidationType, periodType), originalValues);

    // Enable controls for the targeted table only
    controlNames.forEach(name => {
      enableAndRevealErrors(this.statementOfChangesInEquityForm.get(name), { emitEvent: true });
    });

    this.stateService.markSheetEditing(SHEET_KEYS.statementOfChangesInEquity);
    this.rebuildValidationCaches();
  }

  /**
   * Save edit mode (Tick) — subset-persist, calc, validate (same + cross), reconcile, clear editing.
   */
  async saveEditMode(control: FieldControl, consolidationType?: string, periodType?: string): Promise<void> {
    const editKey = this.getEditKey(control, consolidationType, periodType);
    if (!this.editingRows.has(editKey)) {
      return;
    }

    const controlNames = this.collectEditableControlNames(control, consolidationType, periodType);

    // Mark controls as pristine and disable them
    controlNames.forEach(name => {
      const formControl = this.statementOfChangesInEquityForm.get(name);
      if (formControl) {
        formControl.markAsPristine();
        formControl.disable();
      }
    });

    // Remove from editing set
    this.frozenRowStatus.delete(`${control.qname}::${consolidationType ?? ''}::${periodType ?? ''}`);
    this.editingRows.delete(editKey);

    // Recalculate totals FIRST so the persisted subset includes the freshly
    // recalculated calculated/derived totals (downstream sheets read these).
    this.calculateTotals();

    // Subset-persist: this row's controls PLUS the recalculated calculated totals
    const subset = pickSubset(this.statementOfChangesInEquityForm.getRawValue(), [...controlNames, ...this.calculatedTotalKeys]);
    this.stateService.updateSheetData(SHEET_KEYS.statementOfChangesInEquity, subset, true);

    // Full validation tail
    this.validateSameSheet();
    await this.validateCrossSheet();
    this.stateService.reconcileEditedFlag(SHEET_KEYS.statementOfChangesInEquity);
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfChangesInEquity);
    this.evaluateSheetStatus();
  }

  /**
   * Cancel edit mode - always revert to values captured when entering edit mode,
   * discarding any uncommitted typing. editingRows holds the committed values at entry.
   */
  cancelEditMode(control: FieldControl, consolidationType?: string, periodType?: string): void {
    const editKey = this.getEditKey(control, consolidationType, periodType);
    if (!this.editingRows.has(editKey)) {
      return;
    }

    // Revert to the LATEST ADOPTED state rather than the entry-time snapshot, so a
    // sibling tab's confirmed edit that arrived while this row was open is honoured
    // on cancel (P.O. rule 2026-08-17). Falls back to the entry snapshot for any
    // control the state does not carry.
    //
    // In the single-tab case the two are identical — the row is disabled and
    // unwritten for the whole time it is open — so this is a provable no-op there.
    const originalValues = this.editingRows.get(editKey);
    const controlNames = originalValues ? Object.keys(originalValues) : [];
    const adopted = this.stateService.getSheetData(SHEET_KEYS.statementOfChangesInEquity);

    controlNames.forEach((controlName) => {
      const formControl = this.statementOfChangesInEquityForm.get(controlName);
      if (formControl) {
        const revertValue = controlName in adopted
          ? adopted[controlName]
          : (originalValues?.[controlName] ?? null);
        formControl.setValue(revertValue, { emitEvent: false });
        formControl.markAsPristine();
        formControl.disable();
      }
    });

    // Remove from editing set
    this.frozenRowStatus.delete(`${control.qname}::${consolidationType ?? ''}::${periodType ?? ''}`);
    this.editingRows.delete(editKey);
    // ----------------------------------
    // Recalculate totals, run same-sheet validation, clear editing flag, evaluate status
    this.calculateTotals();
    this.validateSameSheet();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfChangesInEquity);
    this.evaluateSheetStatus();
    // ----------------------------------
  }

  /**
   * Build the list of form-control names that participate in a single edit-mode session.
   * - Equity-columns mode with table context: only the equity columns of the (consolidation, period) clicked.
   * - Dual-column / legacy mode (no table context): the row's 4 cells across both consolidations and periods,
   *   which is correct there because that row renders as a single <tr> with all 4 cells visible.
   */
  private collectEditableControlNames(control: FieldControl, consolidationType?: string, periodType?: string): string[] {
    if (this.hasEquityColumns() && this.schema?.equityColumns && consolidationType && periodType) {
      return this.schema.equityColumns.map(column =>
        this.getEquityColumnControlName(control.qname, column.qname, consolidationType, periodType)
      );
    }
    return [
      control.qname + '[Consolidated][CurrentYear]',
      control.qname + '[Consolidated][PreviousYear]',
      control.qname + '[Separate][CurrentYear]',
      control.qname + '[Separate][PreviousYear]'
    ];
  }

  /**
   * Validate Non-distributable reserves sum (MPERS only)
   * Uses ColumnSumValidationService to check if Sub-total of non-distributable reserves
   * equals the sum of component columns.
   * This validation is per row (each line item is treated separately)
   */
  private validateColumnSum(): void {
    // Clear previous errors
    this.columnSumErrors.clear();

    // Skip if no schema, no equity columns, or no columnSumValidations defined
    if (!this.schema || !this.hasEquityColumns() || !this.schema.equityColumns) {
      return;
    }

    // Read validation configs from schema - if none defined, skip validation
    const validationConfigs = this.schema.columnSumValidations;
    if (!validationConfigs || validationConfigs.length === 0) {
      return;
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍 Starting column sum validation from schema config');
    // console.log('🔍 columnSumValidations:', JSON.stringify(validationConfigs, null, 2));

    // Get row qnames (non-header controls with equityColumns)
    const rowQnames = this.schema.controls
      .filter(control => !this.isSectionHeader(control) && control.ui.equityColumns)
      .map(control => control.qname);

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍 Row qnames for validation:', rowQnames);

    const consolidationTypes = this.showConsolidatedColumns ? ['Consolidated', 'Separate'] : ['Separate'];
    const periodTypes = this.hasPreviousYear ? ['Current', 'Previous'] : ['Current'];

    // Get form values
    const formValues = this.statementOfChangesInEquityForm.getRawValue();

    // Debug: Log form values for subtotal columns
    const subtotalColumns = ['NonDistributableReserves', 'OtherReserves', 'EquityAttributableToOwners'];
    const subtotalKeys = Object.keys(formValues).filter(key =>
      subtotalColumns.some(col => key.includes(col))
    );
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('🔍 [validateColumnSum] Form values for subtotal columns:');
    // subtotalKeys.slice(0, 10).forEach(key => {
    //   console.log(`   ${key} = ${formValues[key]}`);
    // });
    // console.log(`   (Total ${subtotalKeys.length} subtotal column keys in form)`);

    // Aggregate all cell errors and row errors from all validation configs
    const allCellErrors = new Map<string, string>();
    const allRowErrors = new Map<string, string[]>();

    // Process each validation config from schema
    validationConfigs.forEach(config => {
      const { cellErrors, rowErrors } = this.columnSumValidation.validateColumnSum(
        formValues,
        rowQnames,
        consolidationTypes,
        periodTypes,
        (rowQname, columnQname, consolidationType, periodType) =>
          this.getEquityColumnControlName(rowQname, columnQname, consolidationType, periodType),
        config
      );

      // Merge cell errors
      cellErrors.forEach((error, key) => {
        allCellErrors.set(key, error);
      });

      // Merge row errors
      rowErrors.forEach((errors, rowQname) => {
        const existing = allRowErrors.get(rowQname) || [];
        allRowErrors.set(rowQname, [...existing, ...errors]);
      });
    });

    // Store cell-level errors for red border
    this.columnSumErrors = allCellErrors;

    // Update validationErrors Map with row errors
    rowQnames.forEach(rowQname => {
      const newErrors = allRowErrors.get(rowQname) || [];
      const existingErrors = this.validationErrors.get(rowQname) || [];

      // Filter out old column sum validation errors ONLY (not API mismatch errors)
      // Column sum errors contain "should equal to the sum of" pattern
      // API mismatch errors contain "does not match" pattern - these should be KEPT
      const subTotalLabels = validationConfigs.map(validation => validation.subTotalLabel);
      const otherErrors = existingErrors.filter(error => {
        // Keep the error if it's NOT a column sum validation error
        // Column sum errors have format: "{label}: {subTotalLabel} should equal to the sum of:"
        const isColumnSumError = subTotalLabels.some(label =>
          error.includes(label) && error.includes('should equal to the sum of')
        );
        return !isColumnSumError;
      });

      if (newErrors.length > 0) {
        // Combine with new errors
        const combinedErrors = [...otherErrors, ...newErrors];
        this.validationErrors.set(rowQname, combinedErrors);
      } else {
        // Remove column sum errors if they exist, keep other errors
        if (otherErrors.length > 0) {
          this.validationErrors.set(rowQname, otherErrors);
        } else if (existingErrors.length > 0) {
          this.validationErrors.delete(rowQname);
        }
      }
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On Validate Column Sum] Validation Errors Step #1: ", this.validationErrors);

    // Create new Map reference to trigger Angular change detection
    this.validationErrors = new Map(this.validationErrors);
    this.bumpView();

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On Validate Column Sum] Validation Errors Step #2: ", this.validationErrors);
  }

  /**
   * Validate equity total horizontal sum (MFRS Consolidated only)
   * Checks: EquityMember = EquityAttributableToOwnersOfParentMember
   *                       + OtherComponentsOfEquityMember
   *                       + NoncontrollingInterestsMember
   * Per row, per period (Current/Previous), Consolidated tables ONLY.
   * Excludes row: ifrs-full:IncreaseDecreaseThroughAcquisitionOfSubsidiary
   */
  private validateEquityTotal(): void {
    this.equityTotalErrors.clear();

    // Guard 1: MFRS only
    if (!this.schema || this.schema.taxonomy !== 'FS-MFRS') return;

    // Guard 2: Must have equity columns
    if (!this.hasEquityColumns() || !this.schema.equityColumns) return;

    // Guard 3: Consolidated must be visible
    if (!this.showConsolidatedColumns) return;

    const totalQname = 'ifrs-full:EquityMember';
    const componentQnames = [
      'ifrs-full:EquityAttributableToOwnersOfParentMember',
      'ifrs-full:OtherComponentsOfEquityMember',
      'ifrs-full:NoncontrollingInterestsMember',
    ];
    const excludedRowQname = 'ifrs-full:IncreaseDecreaseThroughAcquisitionOfSubsidiary';

    // Only Consolidated
    const consolidationTypes = ['Consolidated'];
    const periodTypes = this.hasPreviousYear ? ['Current', 'Previous'] : ['Current'];

    // Get row qnames (non-header, equityColumns, excluding the excluded row)
    const rowQnames = this.schema.controls
      .filter(control =>
        !this.isSectionHeader(control) &&
        control.ui.equityColumns &&
        control.qname !== excludedRowQname
      )
      .map(control => control.qname);

    const formValues = this.statementOfChangesInEquityForm.getRawValue();

    // Unique identifier for this validation's error messages (for filtering)
    const ERROR_MARKER = 'Total should equal to the sum of';

    rowQnames.forEach(rowQname => {
      consolidationTypes.forEach(consolidationType => {
        periodTypes.forEach(periodType => {
          // Get the Total (EquityMember) value
          const totalControlName = this.getEquityColumnControlName(
            rowQname, totalQname, consolidationType, periodType
          );
          const totalValue = parseFloat(formValues[totalControlName]) || 0;

          // Sum the 3 component columns
          let componentSum = 0;
          componentQnames.forEach(cqname => {
            const cControlName = this.getEquityColumnControlName(
              rowQname, cqname, consolidationType, periodType
            );
            componentSum += parseFloat(formValues[cControlName]) || 0;
          });

          // Round to cents, flag any difference (≥ 0.01): the equity Total must
          // equal the sum of its component columns (raw `> 0.01` needed ≥ 0.02).
          if (differsAtCentPrecision(totalValue, componentSum)) {
            // Cell-level error (for red border on the Total cell)
            this.equityTotalErrors.set(totalControlName,
              `Value does not match expected sum (${componentSum.toFixed(2)})`
            );

            // Row-level error (for Error Description column)
            const label = `Total [${consolidationType} ${periodType}]`;
            const errorMsg =
              `${label}: ${ERROR_MARKER}:\n- Equity attributable to owners\n- Equity, others components\n- Non-controlling interests`;

            const existing = this.validationErrors.get(rowQname) || [];
            // Remove old equity total errors for this table context first
            const filtered = existing.filter(message => !(
              message.includes(ERROR_MARKER) &&
              message.includes(`[${consolidationType} ${periodType}]`)
            ));
            filtered.push(errorMsg);
            this.validationErrors.set(rowQname, filtered);
          } else {
            // No error: clean up old equity total errors for this table context
            const existing = this.validationErrors.get(rowQname) || [];
            const filtered = existing.filter(message => !(
              message.includes(ERROR_MARKER) &&
              message.includes(`[${consolidationType} ${periodType}]`)
            ));
            if (filtered.length > 0) {
              this.validationErrors.set(rowQname, filtered);
            } else if (existing.length > 0) {
              this.validationErrors.delete(rowQname);
            }
          }
        });
      });
    });

    // Trigger change detection
    this.validationErrors = new Map(this.validationErrors);
    this.bumpView();
  }

  /**
   * Check if a specific cell has an equity total horizontal validation error (cache lookup)
   */
  hasEquityTotalError(control: FieldControl, columnQname: string,
                      consolidationType: string, periodType: string): boolean {
    this.viewVersion();
    const key = `${control.qname}::${columnQname}::${consolidationType}::${periodType}`;
    return this.cellValidationCache.get(key)?.equityTotal ?? false;
  }

  /**
   * Compute if a specific cell has an equity total horizontal validation error (full logic)
   * Used to add red border to the Total (EquityMember) input field
   * Only applies in MFRS Consolidated tables
   */
  private _computeHasEquityTotalError(control: FieldControl, columnQname: string,
                      consolidationType: string, periodType: string): boolean {
    if (consolidationType !== 'Consolidated') return false;
    if (!this.schema || this.schema.taxonomy !== 'FS-MFRS') return false;
    if (columnQname !== 'ifrs-full:EquityMember') return false;

    const controlName = this.getEquityColumnControlName(
      control.qname, columnQname, consolidationType, periodType
    );
    return this.equityTotalErrors.has(controlName);
  }

  /**
   * Check if a specific cell has an Equity Beginning/End period mismatch error.
   * For equity-columns mode: checks by rowQname + columnQname + consolidationType + periodType.
   * For dual-column mode: checks by controlName (qname + suffix).
   */
  hasEquityBeginningEndError(control: FieldControl, columnQname: string,
                             consolidationType: string, periodType: string): boolean {
    this.viewVersion();
    if (!control.qname.includes('Equity:Beginning') && !control.qname.includes('Equity:End')) {
      return false; // Quick exit for non-equity rows
    }
    const controlName = this.getEquityColumnControlName(
      control.qname, columnQname, consolidationType, periodType
    );
    return this.equityBeginningEndErrors.has(controlName);
  }

  /**
   * Check if a specific dual-column cell has an Equity Beginning/End period mismatch error.
   */
  hasEquityBeginningEndErrorDual(control: FieldControl, suffix: string): boolean {
    this.viewVersion();
    return this.equityBeginningEndErrors.has(control.qname + suffix);
  }

  /**
   * Get validation status for a row in a specific table context
   * Get validation status for a row in a specific table context (cache lookup)
   * Returns: 'Failed' | 'Edited' | 'Validated*' | 'Validated'
   * 'Validated*' indicates data is acceptable but has a mismatch with the baseline
   */
  getRowValidationStatus(control: FieldControl, consolidationType?: string, periodType?: string): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    this.viewVersion();
    const key = `${control.qname}::${consolidationType ?? ''}::${periodType ?? ''}`;
    if (this.isRowInEditMode(control, consolidationType, periodType)) {
      const frozen = this.frozenRowStatus.get(key);
      if (frozen !== undefined) return frozen;
    }
    return this.rowValidationStatusCache.get(key) ?? 'Validated';
  }

  /**
   * Row background colour intent for a table context (cache lookup).
   * 'error' → red (`row-has-error`), 'warning' → yellow
   * (`row-has-validation-error`), 'none' → no highlight. Drives the row
   * background independently of the Mapping Status so entering edit mode (which
   * forces the status to 'Edited') never repaints the row. Red outranks yellow.
   */
  getRowColorIntent(control: FieldControl, consolidationType?: string, periodType?: string): 'error' | 'warning' | 'none' {
    this.viewVersion();
    const key = `${control.qname}::${consolidationType ?? ''}::${periodType ?? ''}`;
    return this.rowColorIntentCache.get(key) ?? 'none';
  }

  /**
   * Compute validation status for a row in a specific table context (full logic).
   * Delegates Edited / Validated-star / Validated resolution to MappingStatusService;
   * the component retains responsibility for computing hasError and
   * hasSuggestedChangeWarning from its equity/dual-column structure.
   */
  private _computeRowValidationStatus(control: FieldControl, consolidationType?: string, periodType?: string): 'Failed' | 'Edited' | 'Validated*' | 'Validated' {
    // Only check monetary items and equity columns
    if (control.ui.controlType !== 'number' && !control.ui.equityColumns) return 'Validated';

    const controlNames = this._buildRowControlNames(control, consolidationType, periodType);

    // Snapshot of current form values for this row
    const currentValues: Record<string, unknown> = {};
    controlNames.forEach(name => {
      currentValues[name] = this.statementOfChangesInEquityForm.get(name)?.value ?? null;
    });

    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control, consolidationType, periodType);

    return this.mappingStatus.computeRowStatus({
      controlNames,
      currentValues,
      lastSubmissionValues: this.mappingStatus.getLastSubmissionValues(SHEET_KEYS.statementOfChangesInEquity),
      hasError,
      hasSuggestedChangeWarning,
      forceEdited: false,
      // Calculated equity totals are computed client-side (calculateTotals) and are
      // absent from the persisted baseline, so the deviation check would mark them
      // Edited with zero user edits. They are never user-editable (enterEditMode
      // skips them) — mark them derived to skip the Edited branch.
      derived: this.isCalculatedField(control),
    });
  }

  /**
   * Build the list of form-control names that make up a row for a given table
   * context (equity-columns mode → one per equity column; dual-column mode →
   * the four Consolidated/Separate × Current/Previous controls). Shared by the
   * status, error-flag and colour-intent computations so they stay in lockstep.
   */
  private _buildRowControlNames(control: FieldControl, consolidationType?: string, periodType?: string): string[] {
    if (this.hasEquityColumns() && this.schema?.equityColumns && control.ui.equityColumns && consolidationType && periodType) {
      return this.schema.equityColumns.map(column =>
        this.getEquityColumnControlName(control.qname, column.qname, consolidationType, periodType)
      );
    }
    return [
      control.qname + '[Consolidated][CurrentYear]',
      control.qname + '[Consolidated][PreviousYear]',
      control.qname + '[Separate][CurrentYear]',
      control.qname + '[Separate][PreviousYear]',
    ];
  }

  /**
   * Compute the row's hasError / hasSuggestedChangeWarning flags for a table
   * context. This is the single definition of "does the row have a hard error"
   * vs "only a suggested-change warning" — consumed by both the Mapping Status
   * (which layers the Edited override on top) and the row colour intent (which
   * deliberately ignores edit mode). Keeping it in one place means the red/yellow
   * background can never disagree with the Failed/Validated* badge.
   */
  private _computeRowErrorFlags(
    control: FieldControl,
    consolidationType?: string,
    periodType?: string
  ): { hasError: boolean; hasSuggestedChangeWarning: boolean } {
    const controlNames = this._buildRowControlNames(control, consolidationType, periodType);

    // hasError: form-control invalid, required-but-empty (touched/dirty), or
    // non-API-mismatch validation errors for this table context
    let hasError = false;

    // Form control invalid
    if (controlNames.some(name => {
      const fc = this.statementOfChangesInEquityForm.get(name);
      return fc && fc.invalid && (fc.dirty || fc.touched);
    })) {
      hasError = true;
    }

    // Required field empty (only if row has been touched/dirtied)
    if (!hasError && this.isRequiredField(control)) {
      const hasAnyTouchedOrDirty = controlNames.some(name => {
        const fc = this.statementOfChangesInEquityForm.get(name);
        return fc && (fc.touched || fc.dirty);
      });
      const hasEmptyRequired = controlNames.some(name => this.isRequiredFieldEmpty(name));
      if (hasEmptyRequired && hasAnyTouchedOrDirty) hasError = true;
    }

    // Non-API-mismatch validation errors for this table context
    if (!hasError && this.validationErrors.has(control.qname)) {
      const allErrors = this.validationErrors.get(control.qname) || [];
      let relevantErrors = allErrors;
      if (consolidationType && periodType) {
        const tableIdentifier = `${consolidationType} ${periodType}`;
        relevantErrors = allErrors.filter(error =>
          error.startsWith(`${tableIdentifier}:`) ||
          error.includes(`[${tableIdentifier}]`)
        );
      }
      if (relevantErrors.some(error => !error.includes('does not match the extracted sum'))) {
        hasError = true;
      }
    }

    // Column sum validation errors for this table context
    if (!hasError && this.validationErrors.has(control.qname) && this.schema?.columnSumValidations) {
      const errors = this.validationErrors.get(control.qname) || [];
      const subTotalLabels = this.schema.columnSumValidations.map(validation => validation.subTotalLabel);
      if (consolidationType && periodType) {
        const tableIdentifier = `${consolidationType} ${periodType}`;
        const hasErrorForThisTable = errors.some(error =>
          subTotalLabels.some(label => error.includes(label)) &&
          error.includes('should equal to the sum of') &&
          (error.startsWith(`${tableIdentifier}:`) || error.includes(`[${tableIdentifier}]`))
        );
        if (hasErrorForThisTable) hasError = true;
      }
    }

    // hasSuggestedChangeWarning: all validation errors for this table context are API-mismatch warnings
    // (role19 has no linkTo nuance — no hasLinkTo guard needed)
    let hasSuggestedChangeWarning = false;
    if (this.validationErrors.has(control.qname)) {
      const allErrors = this.validationErrors.get(control.qname) || [];
      let relevantErrors = allErrors;
      if (consolidationType && periodType) {
        const tableIdentifier = `${consolidationType} ${periodType}`;
        relevantErrors = allErrors.filter(error =>
          error.startsWith(`${tableIdentifier}:`) ||
          error.includes(`[${tableIdentifier}]`)
        );
      }
      hasSuggestedChangeWarning = relevantErrors.length > 0 &&
        relevantErrors.every(message => message.includes('does not match the extracted sum'));
    }

    return { hasError, hasSuggestedChangeWarning };
  }

  /**
   * Compute the row background colour intent for a table context: 'error' (red)
   * when the row has a hard error, 'warning' (yellow) when it only has a
   * suggested-change warning, 'none' otherwise. Red outranks yellow. Unlike the
   * Mapping Status this does NOT consult edit mode, so clicking the edit pencil
   * never repaints the row.
   */
  private _computeRowColorIntent(control: FieldControl, consolidationType?: string, periodType?: string): 'error' | 'warning' | 'none' {
    if (control.ui.controlType !== 'number' && !control.ui.equityColumns) return 'none';
    const { hasError, hasSuggestedChangeWarning } = this._computeRowErrorFlags(control, consolidationType, periodType);
    if (hasError) return 'error';
    if (hasSuggestedChangeWarning) return 'warning';
    return 'none';
  }

  /**
   * Get error description for a row in a specific table context (cache lookup)
   */
  getRowErrorDescription(control: FieldControl, consolidationType?: string, periodType?: string): string {
    this.viewVersion();
    const key = `${control.qname}::${consolidationType ?? ''}::${periodType ?? ''}`;
    return this.rowErrorDescriptionCache.get(key) ?? '';
  }

  /**
   * Get the {errors, warnings} counts for a row in a specific table context
   * (cache lookup). Mirrors getRowErrorDescription() — it pulls from the
   * parallel rowErrorCountCache that _computeRowErrorDescription() populates,
   * so the counts always match the full error list that would otherwise be
   * rendered. Consumed only by the Ctrl+Alt+E compressed-error view.
   */
  getRowErrorCounts(control: FieldControl, consolidationType?: string, periodType?: string): { errors: number; warnings: number } {
    this.viewVersion();
    const key = `${control.qname}::${consolidationType ?? ''}::${periodType ?? ''}`;
    return this.rowErrorCountCache.get(key) ?? { errors: 0, warnings: 0 };
  }

  /**
   * Pure formatter for the Ctrl+Alt+E compressed-error view. Renders the
   * counts as "X errors, Y warnings" with simple singular/plural handling.
   * Returns an empty string when a row has neither, so clean rows stay
   * visually clean — matching today's empty error-description behaviour.
   */
  formatCompressedErrors({ errors, warnings }: { errors: number; warnings: number }): string {
    if (errors === 0 && warnings === 0) return '';
    const errorLabel = errors === 1 ? 'error' : 'errors';
    const warningLabel = warnings === 1 ? 'warning' : 'warnings';
    return `${errors} ${errorLabel}, ${warnings} ${warningLabel}`;
  }

  /**
   * Compute error description for a row in a specific table context (full logic)
   * Filters errors to only show those relevant to the current table
   * Groups errors by category: "Error" (red) and "* Suggested Change" (black)
   */
  private _computeRowErrorDescription(control: FieldControl, consolidationType?: string, periodType?: string): string {
    // Key shared with rowErrorDescriptionCache / rowErrorCountCache so the
    // compressed-count view (Ctrl+Alt+E) can never drift from the rendered text.
    const countKey = `${control.qname}::${consolidationType ?? ''}::${periodType ?? ''}`;

    // Only check monetary items and equity columns
    if (control.ui.controlType !== 'number' && !control.ui.equityColumns) {
      // Keep the count cache consistent with the empty description.
      this.rowErrorCountCache.set(countKey, { errors: 0, warnings: 0 });
      return '';
    }

    const errors: string[] = [];           // Red - Error category
    const suggestedChanges: string[] = []; // Black - * Suggested Change category

    if (this.isRequiredField(control)) {
      // Only collect required errors if the row has been interacted with
      if (this.hasEquityColumns() && this.schema?.equityColumns && control.ui.equityColumns && consolidationType && periodType) {
        // Equity columns mode: Check if any required field is empty in this table
        for (const column of this.schema.equityColumns) {
          const controlName = this.getEquityColumnControlName(control.qname, column.qname, consolidationType, periodType);
          if (this.isRequiredFieldEmpty(controlName)) {
            errors.push(`${column.label}: This is a mandatory field`);
          }
        }
      } else {
        // Dual column mode
        const columns = [
          { suffix: '[Consolidated][CurrentYear]', label: 'Consolidated Current' },
          { suffix: '[Consolidated][PreviousYear]', label: 'Consolidated Previous' },
          { suffix: '[Separate][CurrentYear]', label: 'Separate Current' },
          { suffix: '[Separate][PreviousYear]', label: 'Separate Previous' }
        ];
        for (const { suffix, label } of columns) {
          const controlName = control.qname + suffix;
          if (this.isRequiredFieldEmpty(controlName)) {
            errors.push(`${label}: This is a mandatory field`);
          }
        }
      }
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // Replace the control.qname with your desired column name to inspect. (To reduce log spam otherwise)
    // if (control.qname === 'ifrs-smes:Equity:Beginning') {
    //   console.log("[On Compute Row Error Description] Control Name: ", control.qname);
    //   console.log("[On Compute Row Error Description] Has Validation Errors : ", this.validationErrors.has(control.qname));
    // }

    // 2. Check validation errors (from validateAgainstApiData) - categorize based on message content
    // NOTE: Skip mandatory field errors here if the field is a required field, since Section 1 already handles them
    if (this.validationErrors.has(control.qname)) {
      const allValidationErrors = this.validationErrors.get(control.qname) || [];

      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // Replace the control.qname with your desired column name to inspect. (To reduce log spam otherwise)
      // if (control.qname === 'ifrs-smes:Equity:Beginning') {
      //   console.log("[On Compute Row Error Description] All Validation Errors : ", allValidationErrors);
      // }

      // Filter errors for this specific table context
      let filteredErrors = allValidationErrors;

      if (consolidationType && periodType) {
        // Build the table identifier string that appears in error messages
        const tableIdentifier = `${consolidationType} ${periodType}`;

        // Anchor the match to either the prefix form ("Consolidated Current: …")
        // or the bracket form ("… [Consolidated Current]"). A loose `includes`
        // would also match body references like "(Consolidated Previous Year)"
        // and leak the error into unrelated tables.
        filteredErrors = allValidationErrors.filter(error =>
          error.startsWith(`${tableIdentifier}:`) ||
          error.includes(`[${tableIdentifier}]`)
        );
      }

      // Categorize filtered errors and strip [Consolidated/Separate Current/Previous] labels
      // since the table context already indicates which table the error belongs to
      filteredErrors.forEach(errorMsg => {
        // Skip mandatory field errors if this is a required field (Section 1 already handles them)
        // validateRequiredFields() adds errors carrying MANDATORY_MESSAGE.
        if (this.isRequiredField(control) && errorMsg.includes(MANDATORY_MESSAGE)) {
          return; // Skip this error as Section 1 already handles required field errors
        }

        // Strip table context identifiers from error messages
        // Handles both bracket format "[Consolidated Current]" (from validateRequiredFields)
        // and prefix format "Consolidated Current: " (from validateAllEqualityRules)
        let cleanedMsg = errorMsg
          .replace(/\s*\[Consolidated Current\]/g, '')
          .replace(/\s*\[Consolidated Previous\]/g, '')
          .replace(/\s*\[Separate Current\]/g, '')
          .replace(/\s*\[Separate Previous\]/g, '')
          .replace(/^Consolidated Current:\s*/g, '')
          .replace(/^Consolidated Previous:\s*/g, '')
          .replace(/^Separate Current:\s*/g, '')
          .replace(/^Separate Previous:\s*/g, '');

        // API mismatch warnings go to suggestedChanges (black)
        if (cleanedMsg.includes('does not match the extracted sum')) {
          suggestedChanges.push(cleanedMsg);
        } else {
          // Cross-sheet errors and other validation errors go to errors (red)
          errors.push(cleanedMsg);
        }
      });
    }

    // 3. Collect form control validation errors -> Error category
    // NOTE: Skip 'required' errors here if the field is a required field, since Section 1 already handles them
    const isRequired = this.isRequiredField(control);

    if (this.hasEquityColumns() && this.schema?.equityColumns && control.ui.equityColumns && consolidationType && periodType) {
      // Equity columns mode: Check only the current table's columns
      this.schema.equityColumns.forEach(column => {
        const controlName = this.getEquityColumnControlName(control.qname, column.qname, consolidationType, periodType);
        const formControl = this.statementOfChangesInEquityForm.get(controlName);
        const label = `${column.label}`;

        if (formControl && formControl.invalid && (formControl.dirty || formControl.touched)) {
          const errorMessages: string[] = [];

          if (formControl.errors) {
            // Skip 'required' error if this is a required field (already handled in Section 1)
            if (formControl.errors['required'] && !isRequired) errorMessages.push('This is a mandatory field');
            if (formControl.errors['min']) errorMessages.push(`Min: ${formControl.errors['min'].min}`);
            if (formControl.errors['max']) errorMessages.push(`Max: ${formControl.errors['max'].max}`);
          }

          if (errorMessages.length > 0) {
            errors.push(`${label}: ${errorMessages.join(', ')}`);
          }
        }
      });
    } else {
      // Dual column mode - Control name format must match form control creation: qname[Consolidated|Separate][CurrentYear|PreviousYear]
      const columns = [
        { suffix: '[Consolidated][CurrentYear]', label: 'Consolidated Current' },
        { suffix: '[Consolidated][PreviousYear]', label: 'Consolidated Previous' },
        { suffix: '[Separate][CurrentYear]', label: 'Separate Current' },
        { suffix: '[Separate][PreviousYear]', label: 'Separate Previous' }
      ];

      for (const { suffix, label } of columns) {
        const controlName = control.qname + suffix;
        const formControl = this.statementOfChangesInEquityForm.get(controlName);

        if (formControl && formControl.invalid && (formControl.dirty || formControl.touched)) {
          const errorMessages: string[] = [];

          if (formControl.errors) {
            // Skip 'required' error if this is a required field (already handled in Section 1)
            if (formControl.errors['required'] && !isRequired) errorMessages.push('This is a mandatory field');
            if (formControl.errors['min']) errorMessages.push(`Min: ${formControl.errors['min'].min}`);
            if (formControl.errors['max']) errorMessages.push(`Max: ${formControl.errors['max'].max}`);
          }

          if (errorMessages.length > 0) {
            errors.push(`${label}: ${errorMessages.join(', ')}`);
          }
        }
      }
    }

    // errors[] and suggestedChanges[] are now finalized — record their counts
    // in the parallel cache so getRowErrorCounts() (Ctrl+Alt+E compressed view)
    // mirrors exactly what the HTML below renders.
    this.rowErrorCountCache.set(countKey, {
      errors: errors.length,
      warnings: suggestedChanges.length,
    });

    // Format output grouped by category with CSS classes
    // Error section in red (.error-text), Suggested Change section in black (.suggested-change-text)
    let result = '';

    if (errors.length > 0) {
      result += '<span class="error-text">ERROR</span><br>';
      result += '<span class="error-text">' + errors.map((errorText, errorIndex) => `${errorIndex + 1}. ${errorText}`).join('<br><br>') + '</span>';
    }

    if (suggestedChanges.length > 0) {
      if (result) result += '<br><br>';
      result += '<span class="error-text">*</span><span class="suggested-change-text"> SUGGESTED CHANGE</span><br>';
      result += '<span class="suggested-change-text">' + suggestedChanges.map((changeText, changeIndex) => `${changeIndex + 1}. ${changeText}`).join('<br><br>') + '</span>';
    }

    return result;
  }

  /**
   * Check if row has any "Error" category errors (red text)
   * Error category includes: required field errors, cross-sheet errors, column sum errors, form validation errors
   */
  hasErrorCategory(control: FieldControl, consolidationType?: string, periodType?: string): boolean {
    if (control.ui.controlType !== 'number' && !control.ui.equityColumns) return false;

    // Determine visible tables based on showConsolidatedColumns and hasPreviousYear
    // Only check tables that are actually visible in the UI
    const consolidationTypes = consolidationType
      ? [consolidationType]
      : (this.showConsolidatedColumns ? ['Consolidated', 'Separate'] : ['Separate']);
    const periodTypes = periodType
      ? [periodType]
      : (this.hasPreviousYear ? ['Current', 'Previous'] : ['Current']);

    // Check required field errors
    if (this.isRequiredField(control)) {
      if (this.hasEquityColumns() && this.schema?.equityColumns && control.ui.equityColumns) {
        // Equity columns mode: For required rows, only check the "Total" column (ifrs-full:EquityMember)
        // This is because a row being "required" means the Total must have a value,
        // not that ALL individual equity columns must have values
        const isMFRS = this.crossRefService.isMFRS();
        const totalColumnQname = isMFRS ? 'ifrs-full:EquityMember' : 'ifrs-smes:EquityMember';

        for (const consType of consolidationTypes) {
          for (const perType of periodTypes) {
            const controlName = this.getEquityColumnControlName(control.qname, totalColumnQname, consType, perType);
            if (this.isRequiredFieldEmpty(controlName)) {
              return true;
            }
          }
        }
      } else {
        // Dual column mode: check visible tables only
        for (const consType of consolidationTypes) {
          for (const perType of periodTypes) {
            const period = perType === 'Current' ? 'CurrentYear' : 'PreviousYear';
            const controlName = control.qname + `[${consType}][${period}]`;
            if (this.isRequiredFieldEmpty(controlName)) {
              return true;
            }
          }
        }
      }
    }

    // Check validation errors for non-API mismatch errors (cross-sheet, column sum, etc.)
    if (this.validationErrors.has(control.qname)) {
      const allErrors = this.validationErrors.get(control.qname) || [];
      let filteredErrors = allErrors;

      if (consolidationType && periodType) {
        const tableIdentifier = `${consolidationType} ${periodType}`;
        // Anchor on prefix or bracket form to avoid body-text collisions.
        filteredErrors = allErrors.filter(error =>
          error.startsWith(`${tableIdentifier}:`) ||
          error.includes(`[${tableIdentifier}]`)
        );
      }

      // Check if any error is NOT an API mismatch warning
      if (filteredErrors.some(errorMsg => !errorMsg.includes('does not match the extracted sum'))) {
        return true;
      }
    }

    // Check form control validation errors
    if (this.hasEquityColumns() && this.schema?.equityColumns && control.ui.equityColumns) {
      // Equity columns mode: check each equity column across visible tables
      for (const consType of consolidationTypes) {
        for (const perType of periodTypes) {
          for (const column of this.schema.equityColumns) {
            const controlName = this.getEquityColumnControlName(control.qname, column.qname, consType, perType);
            const formControl = this.statementOfChangesInEquityForm.get(controlName);
            if (formControl && formControl.invalid && (formControl.dirty || formControl.touched)) {
              return true;
            }
          }
        }
      }
    } else {
      // Dual column mode: check visible tables only
      for (const consType of consolidationTypes) {
        for (const perType of periodTypes) {
          const period = perType === 'Current' ? 'CurrentYear' : 'PreviousYear';
          const controlName = control.qname + `[${consType}][${period}]`;
          const formControl = this.statementOfChangesInEquityForm.get(controlName);
          if (formControl && formControl.invalid && (formControl.dirty || formControl.touched)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Check if row has any "Suggested Change" category errors (black text)
   * Suggested Change category includes: API value mismatch warnings
   */
  hasSuggestedChangeCategory(control: FieldControl, consolidationType?: string, periodType?: string): boolean {
    if (control.ui.controlType !== 'number' && !control.ui.equityColumns) return false;

    if (!this.validationErrors.has(control.qname)) {
      return false;
    }

    const allErrors = this.validationErrors.get(control.qname) || [];
    let filteredErrors = allErrors;

    if (consolidationType && periodType) {
      const tableIdentifier = `${consolidationType} ${periodType}`;
      // Anchor on prefix or bracket form to avoid body-text collisions.
      filteredErrors = allErrors.filter(error =>
        error.startsWith(`${tableIdentifier}:`) ||
        error.includes(`[${tableIdentifier}]`)
      );
    }

    // Check if any error IS an API mismatch warning
    return filteredErrors.some(errorMsg => errorMsg.includes('does not match the extracted sum'));
  }

  /**
   * Check if row has failed validation
   */
  hasRowFailed(control: FieldControl): boolean {
    return this._computeRowValidationStatus(control) === 'Failed';
  }

  /**
   * Evaluate sheet status by looping through all visible tables' rows mapping status.
   * For equity columns mode, loops through all permutations (consolidated/separate x current/previous).
   * For dual column mode (legacy), loops through controls without permutation parameters.
   * Sets hasErrors = true if any row has 'Failed' status, false otherwise.
   * Called at init and before submit.
   */
  evaluateSheetStatus(): void {
    if (!this.schema?.controls) return;

    let hasErrors = false;

    if (this.hasEquityColumns()) {
      // Equity columns mode: loop through all visible table permutations
      const consolidationTypes = this.showConsolidatedColumns ? ['Consolidated', 'Separate'] : ['Separate'];
      const periodTypes = this.hasPreviousYear ? ['Current', 'Previous'] : ['Current'];

      for (const consolidationType of consolidationTypes) {
        if (hasErrors) break;
        for (const periodType of periodTypes) {
          if (hasErrors) break;
          for (const control of this.schema.controls) {
            if (this.getRowValidationStatus(control, consolidationType, periodType) === 'Failed') {
              hasErrors = true;
              break;
            }
          }
        }
      }
    } else {
      // Dual column mode (legacy): loop through controls without permutation parameters
      for (const control of this.schema.controls) {
        if (this.getRowValidationStatus(control) === 'Failed') {
          hasErrors = true;
          break;
        }
      }
    }

    this.stateService.updateSheetLevelStatus(SHEET_KEYS.statementOfChangesInEquity, { hasErrors });
  }

  /**
   * Check if row has validation error for a specific table context (for yellow highlighting)
   * Filters errors to check only those relevant to the current table
   * Check if row has validation error for a specific table context (cache lookup)
   */
  hasRowValidationError(control: FieldControl, consolidationType?: string, periodType?: string): boolean {
    this.viewVersion();
    const key = `${control.qname}::${consolidationType ?? ''}::${periodType ?? ''}`;
    return this.rowHasValidationErrorCache.get(key) ?? false;
  }

  /**
   * Compute if row has validation error for a specific table context (full logic)
   */
  private _computeHasRowValidationError(control: FieldControl, consolidationType?: string, periodType?: string): boolean {
    return this.hasRowValidationErrorForTable(control, consolidationType, periodType);
  }

  /**
   * Helper method to check if there are validation errors for a specific table context
   * Returns true if there are errors for this specific table
   */
  private hasRowValidationErrorForTable(control: FieldControl, consolidationType?: string, periodType?: string): boolean {
    if (!this.validationErrors.has(control.qname)) {
      return false;
    }

    const allValidationErrors = this.validationErrors.get(control.qname) || [];

    // If no table context provided, check if there are any errors at all
    if (!consolidationType || !periodType) {
      return allValidationErrors.length > 0;
    }

    // Filter errors for this specific table context
    const tableIdentifier = `${consolidationType} ${periodType}`;

    // Anchor the match to either the prefix form ("Consolidated Current: …")
    // or the bracket form ("… [Consolidated Current]"). A loose `includes`
    // would also match body references like "(Consolidated Previous Year)"
    // and leak the error into unrelated tables.
    const filteredErrors = allValidationErrors.filter(error =>
      error.startsWith(`${tableIdentifier}:`) ||
      error.includes(`[${tableIdentifier}]`)
    );

    return filteredErrors.length > 0;
  }

  /**
   * Check if a specific equity column has a cross-sheet validation error (cache lookup)
   */
  hasCrossSheetError(rowQname: string, columnQname: string, consolidationType: string, periodType: string): boolean {
    this.viewVersion();
    const key = `${rowQname}::${columnQname}::${consolidationType}::${periodType}`;
    return this.cellValidationCache.get(key)?.crossSheet ?? false;
  }

  /**
   * Compute if a specific equity column has a cross-sheet validation error (full logic)
   * Used to add red border to individual input fields for cross-sheet mismatches
   * Applies to ComprehensiveIncome, OtherComprehensiveIncome, and ProfitLoss rows
   */
  private _computeHasCrossSheetError(rowQname: string, columnQname: string, consolidationType: string, periodType: string): boolean {
    // Determine if MFRS or MPERS
    const isMFRS = this.crossRefService.isMFRS();
    const comprehensiveIncomeQname = isMFRS
      ? 'ifrs-full:ComprehensiveIncome'
      : 'ifrs-smes:ComprehensiveIncome';
    const otherComprehensiveIncomeQname = isMFRS
      ? 'ifrs-full:OtherComprehensiveIncome'
      : 'ifrs-smes:OtherComprehensiveIncome';
    const equityMemberQname = isMFRS
      ? 'ifrs-full:EquityMember'
      : 'ifrs-smes:EquityMember';

    // Every cross-sheet equality rule on this sheet compares ONLY the Total
    // (EquityMember) column: buildFieldKey() in validation-field-key.util.ts
    // hardcodes the EquityMember suffix for COLUMN_FORMAT.EQUITY sheets, so the
    // sub-columns are never operands of the comparison that raised the message.
    // This gate is universal — it is checked BEFORE any row test, because it
    // holds for every cross-sheet rule the sheet can raise.
    if (columnQname !== equityMemberQname) return false;

    const tableIdentifier = `${consolidationType} ${periodType}`;
    // Anchor on prefix or bracket form to avoid body-text collisions
    // (e.g. "(Consolidated Previous Year)" inside another table's message).
    const matchesThisTable = (msg: string | undefined | null): boolean =>
      !!msg && (msg.startsWith(`${tableIdentifier}:`) || msg.includes(`[${tableIdentifier}]`));

    // `equalityValidationErrors` is this sheet's AUTHORITATIVE cross-sheet source
    // (runCrossSheetEqualityChecks says so, and rebuildEqualityMessages mirrors it
    // into validationErrors for the text). It is consulted for EVERY row.
    //
    // It used to be read for ProfitLoss alone, behind a hard-coded three-row
    // allow-list (ProfitLoss / ComprehensiveIncome / OtherComprehensiveIncome).
    // That silently excluded `Equity:Beginning` and `Equity:End`, whose
    // reconciliation against the SOFP `Total equity` is written here by a
    // DIFFERENT producer — EquityReconciliationService.validateCrossSheetEquityByYear
    // — so a real, rendered mismatch on the Total cell had no border. P.O. ruling
    // 2026-08-26: the border appears whenever the text appears. Do not reintroduce
    // a row allow-list; gate on the column and let the error maps decide.
    // Pinned by `role19-equity-end-crosssheet-border.spec.ts`.
    const equalityErrors = this.equalityValidationErrors.get(rowQname);
    if (equalityErrors?.some((err: any) => matchesThisTable(err?.message))) {
      return true;
    }

    // ComprehensiveIncome and OtherComprehensiveIncome ALSO mirror their role-15
    // mismatch into validationErrors; keep reading that as a second source for
    // those two rows only, since it is where their message demonstrably lands.
    if (rowQname !== comprehensiveIncomeQname && rowQname !== otherComprehensiveIncomeQname) {
      return false;
    }

    // Check if this qname has validation errors
    if (!this.validationErrors.has(rowQname)) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`🔍 hasCrossSheetError [${consolidationType} ${periodType}]: No validation errors in Map for ${rowQname}`);
      return false;
    }

    const errors = this.validationErrors.get(rowQname) || [];

    // ONE message must satisfy both halves — it is a role-15 cross-sheet mismatch
    // AND it belongs to this table. Testing the two independently (as this used
    // to) let a SoCI message in one table and an unrelated message in another
    // combine to paint a cell that has neither.
    return errors.some(
      (error) => error.includes('Statement of Comprehensive Income') && matchesThisTable(error)
    );
  }

  /**
   * Rebuild all validation caches by iterating schema.controls once.
   * Called after each validation pass so template bindings do Map.get() lookups.
   */
  private rebuildValidationCaches(): void {
    if (!this.schema) return;

    // Clear all caches
    this.rowValidationStatusCache.clear();
    this.rowErrorDescriptionCache.clear();
    this.rowErrorCountCache.clear();
    this.cellValidationCache.clear();
    this.rowHasValidationErrorCache.clear();
    this.rowColorIntentCache.clear();

    const consolidationTypes = this.showConsolidatedColumns ? ['Consolidated', 'Separate'] : ['Separate'];
    const periodTypes = this.hasPreviousYear ? ['Current', 'Previous'] : ['Current'];

    for (const control of this.schema.controls) {
      if (this.isSectionHeader(control)) continue;
      if (control.ui.controlType !== 'number' && !control.ui.equityColumns) continue;

      if (this.hasEquityColumns() && control.ui.equityColumns) {
        // Equity columns mode: cache per table
        for (const consolidationType of consolidationTypes) {
          for (const periodType of periodTypes) {
            const rowKey = `${control.qname}::${consolidationType}::${periodType}`;
            this.rowValidationStatusCache.set(rowKey, this._computeRowValidationStatus(control, consolidationType, periodType));
            this.rowErrorDescriptionCache.set(rowKey, this._computeRowErrorDescription(control, consolidationType, periodType));
            this.rowHasValidationErrorCache.set(rowKey, this._computeHasRowValidationError(control, consolidationType, periodType));
            this.rowColorIntentCache.set(rowKey, this._computeRowColorIntent(control, consolidationType, periodType));

            // Cell-level caches
            if (this.schema?.equityColumns) {
              for (const column of this.schema.equityColumns) {
                const cellKey = `${control.qname}::${column.qname}::${consolidationType}::${periodType}`;
                this.cellValidationCache.set(cellKey, {
                  crossSheet: this._computeHasCrossSheetError(control.qname, column.qname, consolidationType, periodType),
                  required: this._computeHasRequiredError(control, column.qname, consolidationType, periodType),
                  columnSum: this._computeHasColumnSumError(control, column.qname, consolidationType, periodType),
                  equityTotal: this._computeHasEquityTotalError(control, column.qname, consolidationType, periodType),
                });
              }
            }
          }
        }
      } else if (control.ui.dualColumn) {
        // Dual column mode: cache with empty consolidationType/periodType
        const rowKey = `${control.qname}::::`;
        this.rowValidationStatusCache.set(rowKey, this._computeRowValidationStatus(control));
        this.rowErrorDescriptionCache.set(rowKey, this._computeRowErrorDescription(control));
        this.rowHasValidationErrorCache.set(rowKey, this._computeHasRowValidationError(control));
        this.rowColorIntentCache.set(rowKey, this._computeRowColorIntent(control));
      }
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Public read surface consumed by the refactored debug layout
  // (Role19DebugLayoutComponent). Kept on the host so the debug layout
  // does not need to reach into the host's private maps.
  // ──────────────────────────────────────────────────────────────────

  getColumnSumValidations(): ColumnSumValidationConfig[] {
    return this.schema?.columnSumValidations ?? [];
  }

  /**
   * Row-level validation messages for a given row qname. This is the same
   * bag that `getRowErrorDescription` pulls from; exposing it lets the
   * debug layout route row-level aggregation errors back onto their
   * subtotal cell as a short chip without duplicating classification logic.
   */
  getRowValidationErrors(rowQname: string): string[] {
    this.viewVersion();
    return this.validationErrors.get(rowQname) ?? [];
  }

  getFormStats(): { dirty: number; touched: number; disabled: number; total: number } {
    const controls = this.statementOfChangesInEquityForm?.controls;
    if (!controls) return { dirty: 0, touched: 0, disabled: 0, total: 0 };
    let dirty = 0, touched = 0, disabled = 0, total = 0;
    for (const key of Object.keys(controls)) {
      const formControl = controls[key];
      total++;
      if (formControl.dirty) dirty++;
      if (formControl.touched) touched++;
      if (formControl.disabled) disabled++;
    }
    return { dirty, touched, disabled, total };
  }

  setFieldValue(controlName: string, value: number | null): boolean {
    const control = this.statementOfChangesInEquityForm?.get(controlName);
    if (!control || control.disabled) return false;
    control.setValue(value, { emitEvent: true });
    control.markAsDirty();
    control.markAsTouched();
    return true;
  }

  onCancelConfirmed(): void {
    // Clear all editing states
    this.editingRows.clear();
    this.frozenRowStatus.clear();
    this.stateService.clearSheetEditing(SHEET_KEYS.statementOfChangesInEquity);

    // Clear all validation error Maps
    // (reassigning triggers Angular change detection for ngClass bindings)
    this.validationErrors = new Map<string, string[]>();
    this.columnSumErrors.clear(); // Clear column sum errors to remove red borders
    this.equityTotalErrors.clear(); // Clear equity total errors to remove red borders
    this.equalityValidationErrors.clear(); // Clear equality validation errors

    // Mark form as pristine and untouched to hide error messages
    // NOTE: Do NOT call loadSavedState() here - the form has already been patched
    // by submit-for-validation.revertToInitialValues() with proper null-to-0 conversion
    this.statementOfChangesInEquityForm.markAsPristine();
    this.statementOfChangesInEquityForm.markAsUntouched();

    // Also mark all individual controls as untouched
    Object.keys(this.statementOfChangesInEquityForm.controls).forEach((key) => {
      const control = this.statementOfChangesInEquityForm.get(key);
      if (control) {
        control.markAsPristine();
        control.markAsUntouched();
      }
    });

    // Force change detection to update ngClass bindings (red borders)
    this.bumpView();

    // Reset validation flag before recalculating
    this.isSyncValidationComplete.set(false);
    this.isAsyncValidationComplete = false;

    // Recalculate totals for calculated fields, but skip validations
    // to avoid showing errors immediately after cancel
    setTimeout(() => {
      this.calculateTotals();
      this.isSyncValidationComplete.set(true);
      this.isAsyncValidationComplete = true;
      this.rebuildValidationCaches();
      this.bumpView();
    }, 100);
  }

  /**
   * Memoised per-control numeric policy — this sheet's single declaration
   * point for decimals, grouping and the integer-digit cap, all resolved from
   * the control's own schema record rather than hand-written per binding.
   *
   * Memoised because templates call this once per binding inside an `*ngFor`;
   * an unmemoised `resolveNumericPolicy` would re-resolve on every
   * change-detection cycle. Keyed by `qname`, unique per control in a sheet.
   *
   * `[min]`/`[max]` are deliberately NOT bound from here — `SchemaNumericDirective`
   * assigns them onto the PrimeNG instance directly. See that directive's class
   * docblock for why a template binding coerces an unconstrained `undefined`
   * to `NaN` and silently blocks the minus key on signed monetary fields.
   */
  private policyCache = new Map<string, NumericFieldPolicy>();

  numericPolicy(control: FieldControl): NumericFieldPolicy {
    const cached = this.policyCache.get(control.qname);
    if (cached)
      return cached;
    const resolved = resolveNumericPolicy(control);
    this.policyCache.set(control.qname, resolved);
    return resolved;
  }

}
